/**
 * KOPS-468: Subscriber Management Dashboard
 * aisearchportal.com/admin/subscribers
 * Navy + Gold split-panel layout (matches ContactsPage pattern)
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Button, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch, faUsers, faDollarSign, faChartLine, faCrown,
  faRocket, faStar, faEnvelope, faPhone, faGlobe, faDownload,
  faExchangeAlt, faCalendarPlus, faPowerOff, faExternalLinkAlt,
  faClock, faPlug, faAddressBook, faRobot, faShieldAlt, faExclamationTriangle,
  faCheckCircle, faDatabase, faBuilding, faUserShield, faHome, faComments, faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { faStripeS } from '@fortawesome/free-brands-svg-icons';
import axiosInstance from '../../helpers/api';

interface Subscriber {
  portal_id: string;
  user_id: string;
  subdomain: string;
  plan_id: string;
  status: string;
  trial_ends_at: string | null;
  subscription_id: string | null;
  created_at: string;
  user_first_name: string;
  user_last_name: string;
  user_email: string;
  user_phone: string | null;
  last_login: string | null;
  stripe_customer_id: string | null;
  user_role: string | null;
  connected_emails: number;
  lead_count: number;
  // KOPS-465 brokerage relationship (stacked layer) — populated by the
  // list endpoint via LEFT JOIN to brokerage_team_members.
  brokerage_name?: string | null;
  brokerage_id?: string | null;
  member_role?: string | null;
  // KOPS-468 account type for unified subscriber list
  account_type?: string;
  // KOPS-501 Pipeline orchestrator status
  pipeline_status?: string;
  pipeline_health_score?: number | null;
  pipeline_last_run?: string | null;
  classified_count?: number;
  stuck_count?: number;
  buyer_profiles?: number;
  seller_profiles?: number;
  // KOPS-539 onboarding visibility
  profile_complete?: boolean | null;
  stuck_reason?: 'profile_empty' | 'no_email_connected' | 'pipeline_never_ran' | 'classification_zero' | null;
}

interface PipelineRun {
  run_id: string;
  started_at: string | null;
  completed_at: string | null;
  status: string;
  trigger: string;
  steps: Array<{
    step: string;
    status: string;
    timestamp: string;
    details: Record<string, any>;
  }>;
  health_score: number | null;
  issues: Array<{ severity: string; step: string; message: string }>;
  recommendation: string | null;
  elapsed_seconds?: number;
  failed_step?: string;
  error?: string;
}

interface BrokerageRelationship {
  team_member_id: string;
  brokerage_id: string;
  brokerage_name: string;
  brokerage_email?: string | null;
  brokerage_plan_type?: string | null;
  member_role: string;
  is_admin: boolean;
  team_plan: string;
  monthly_fee: number;
  joined_at?: string | null;
}

interface SubscriberDetail {
  subscriber: Subscriber & {
    portal_created_at: string;
    user_created_on: string;
    brand_color: string | null;
    logo_url: string | null;
    welcome_message: string | null;
    contact_info: any;
    social_links: any;
    features: any;
  };
  connectedEmails: Array<{ email: string; provider: string; created_on: string }>;
  leadCount: number;
  aiUsage: { total_requests: number; total_tokens: number; total_cost: number };
  coachActivity: number;
  brokerageRelationship?: BrokerageRelationship | null;
}

interface Stats {
  total: number;
  hub_count: number;
  search_count: number;
  pro_count: number;
  broker_count?: number;
  total_agents?: number;
  total_brokers?: number;
  total_internal?: number;
  trial_count: number;
  active_count: number;
  inactive_count: number;
  mrr: number;
}

const colors = {
  navy: '#0F1E35', navy2: '#162844', navy3: '#1E3557',
  gold: '#C9952A', gold2: '#E8B44A', gold3: '#F5D080',
  white: '#FFFFFF', off: '#E8EEF6', muted: '#8FA3BF',
  green: '#27ae60', red: '#e74c3c', orange: '#f39c12', blue: '#3498db'
};

const PLAN_LABELS: Record<string, string> = {
  'agent-hub': 'Agent Hub',
  'ai-search': 'AI Search',
  'ai-pro': 'AI Pro',
  'broker': 'Broker',
  'internal': 'Internal',
};

const ACCOUNT_TYPE_COLORS: Record<string, string> = {
  'agent': '#3498db',
  'broker': '#C9952A',
  'internal': '#27ae60',
};

// KOPS-465 team-plan labels (stacked layer — broker-provided tiers).
// Separate from PLAN_LABELS because they belong to a different table
// (brokerage_team_members.commission_settings.team_plan).
const TEAM_PLAN_LABELS: Record<string, string> = {
  'team-member': 'Team Member',
  'agent-hub':   'Agent Hub',
  'ai-search':   'AI Search',
  'ai-pro':      'AI Pro',
};
const TEAM_PLAN_INCLUDES: Record<string, string> = {
  'team-member': 'Compliance oversight, AI Review, Broker Chat, Document management',
  'agent-hub':   'Team Member features + Individual CRM + Buyer Portal',
  'ai-search':   'Agent Hub + AI-powered search + AI recommendations',
  'ai-pro':      'AI Search + AI Coach + full automation suite',
};

const PLAN_PRICES: Record<string, number> = {
  'agent-hub': 70,
  'ai-search': 135,
  'ai-pro': 185,
  'broker': 500,
};

const SubscribersPage: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  // KOPS-465 — 'all' | 'under_broker' | 'solo'
  const [filterBrokerage, setFilterBrokerage] = useState('all');
  // KOPS-468 — 'all' | 'agent' | 'broker' | 'internal'
  const [filterUserType, setFilterUserType] = useState('all');
  // Add User modal
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ firstName: '', lastName: '', email: '', password: '', phone: '', accountType: 'agent' as string, brokerageId: '' });
  const [addingUser, setAddingUser] = useState(false);

  // Right panel
  const [selected, setSelected] = useState<Subscriber | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [detail, setDetail] = useState<SubscriberDetail | null>(null);
  const [health, setHealth] = useState<any>(null);
  const [payments, setPayments] = useState<any>(null);
  const [pipelineRuns, setPipelineRuns] = useState<PipelineRun[]>([]);
  const [pipelineRetrying, setPipelineRetrying] = useState(false);
  const [lifecycle, setLifecycle] = useState<any[]>([]);
  // Call
  const [teamCallers, setTeamCallers] = useState<any[]>([]);
  const [showCallPanel, setShowCallPanel] = useState(false);
  const [selectedCaller, setSelectedCaller] = useState('');
  const [calling, setCalling] = useState(false);
  const [lifecycleTokens, setLifecycleTokens] = useState(0);
  const [retryingMilestone, setRetryingMilestone] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [classAudit, setClassAudit] = useState<any>(null);
  const [emailLog, setEmailLog] = useState<any[]>([]);
  const [runningClassification, setRunningClassification] = useState(false);
  // Onboarding report
  const [reportStatus, setReportStatus] = useState<{ exists: boolean; report?: { file_name: string; generated_at: string } | null }>({ exists: false });
  const [reportLoading, setReportLoading] = useState(false);
  // Report email draft
  const [reportEmailDraft, setReportEmailDraft] = useState('');
  const [reportEmailSubject, setReportEmailSubject] = useState('');
  const [reportEmailLoading, setReportEmailLoading] = useState(false);
  const [reportEmailSending, setReportEmailSending] = useState(false);
  const [showReportEmail, setShowReportEmail] = useState(false);
  const [publishingReport, setPublishingReport] = useState(false);
  const [reportPublished, setReportPublished] = useState(false);
  const [publishingOpp, setPublishingOpp] = useState(false);
  const [sendingOppEmail, setSendingOppEmail] = useState(false);
  // KOPS-620: Operations tab
  const [adminTab, setAdminTab] = useState<'account' | 'operations'>('account');
  const [opsData, setOpsData] = useState<any>(null);
  const [opsLoading, setOpsLoading] = useState(false);
  const [activityLog, setActivityLog] = useState<any[]>([]);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [emailPreview, setEmailPreview] = useState<any>(null);

  // Jasper state (mirrors ContactsPage pattern)
  const [jasperTask, setJasperTask] = useState('');
  const [jasperChannel, setJasperChannel] = useState<'sms' | 'email'>('sms');
  const [jasperPreview, setJasperPreview] = useState('');
  const [jasperEmailSubject, setJasperEmailSubject] = useState('');
  const [jasperLoading, setJasperLoading] = useState(false);
  const [jasperSending, setJasperSending] = useState(false);
  const [jasperConversation, setJasperConversation] = useState<any[]>([]);
  const [showJasper, setShowJasper] = useState(false);

  // Actions
  const [alert, setAlertRaw] = useState<{ type: string; message: string } | null>(null);
  const setAlert = (a: { type: string; message: string } | null) => {
    setAlertRaw(a);
    if (a) setTimeout(() => setAlertRaw(null), 5000);
  };
  const [actionLoading, setActionLoading] = useState(false);

  // Trial extend
  const [showTrialExtend, setShowTrialExtend] = useState(false);
  const [trialDays, setTrialDays] = useState(14);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const [supportMessages, setSupportMessages] = useState<any[]>([]);
  const [supportConvId, setSupportConvId] = useState('');
  const [supportMsg, setSupportMsg] = useState('');
  const [supportLoading, setSupportLoading] = useState(false);
  const [sendingSupport, setSendingSupport] = useState(false);

  useEffect(() => { fetchSubscribers(); }, [search, filterStatus, filterPlan, filterBrokerage, filterUserType]);

  const handleAddUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password || !newUser.accountType) return;
    setAddingUser(true);
    try {
      const response = await axiosInstance.post('/admin/subscribers/create-user', newUser);
      if (response.data.success) {
        setShowAddUser(false);
        setNewUser({ firstName: '', lastName: '', email: '', password: '', phone: '', accountType: 'agent', brokerageId: '' });
        setAlert({ type: 'success', message: `${newUser.accountType} account created for ${newUser.email}` });
        fetchSubscribers();
      }
    } catch (err: any) {
      setAlert({ type: 'error', message: err.response?.data?.error || 'Failed to create user' });
    } finally {
      setAddingUser(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterPlan !== 'all') params.set('plan', filterPlan);
      if (filterBrokerage !== 'all') params.set('brokerage', filterBrokerage);
      if (filterUserType !== 'all') params.set('user_type', filterUserType);
      const response = await axiosInstance.get(`/admin/subscribers?${params.toString()}`);
      if (response.data.success) {
        setSubscribers(response.data.subscribers);
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch subscribers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Restore selected subscriber from URL on initial load
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && subscribers.length > 0 && !selected) {
      const match = subscribers.find((s) => s.portal_id === id || s.user_id === id);
      if (match) selectSubscriber(match);
    }
  }, [subscribers]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectSubscriber = async (sub: Subscriber) => {
    setSelected(sub);
    setSearchParams({ id: sub.portal_id || sub.user_id });
    setDetailLoading(true);
    setShowTrialExtend(false);
    setShowCallPanel(false); setSelectedCaller('');
    setAdminTab('account'); setOpsData(null);
    setJasperTask(''); setJasperPreview(''); setJasperEmailSubject('');
    // Fetch Jasper conversation history
    axiosInstance.get(`/admin/subscribers/${sub.user_id}/jasper-conversation`).then(r => {
      if (r.data.success) setJasperConversation(r.data.messages || []);
    }).catch(() => setJasperConversation([]));
    try {
      const response = await axiosInstance.get(`/admin/subscribers/${sub.portal_id}`);
      if (response.data.success) {
        setDetail(response.data);
      }
      // Fetch AI pipeline health
      axiosInstance.get(`/admin/subscribers/${sub.user_id}/health`).then(r => {
        if (r.data.success) setHealth(r.data.health);
      }).catch(() => setHealth(null));
      // Fetch Stripe payments
      axiosInstance.get(`/admin/subscribers/${sub.portal_id}/payments`).then(r => {
        if (r.data.success) setPayments(r.data);
      }).catch(() => setPayments(null));
      // Fetch pipeline runs
      console.log('[ADMIN] Fetching pipeline runs for user_id:', sub.user_id);
      axiosInstance.get(`/admin/subscribers/${sub.user_id}/pipeline-runs`).then(r => {
        console.log('[ADMIN] Pipeline runs response:', r.data.success, 'runs:', r.data.runs?.length);
        if (r.data.success) setPipelineRuns(r.data.runs || []);
      }).catch((err) => { console.error('[ADMIN] Pipeline runs failed:', err.message); setPipelineRuns([]); });
      // Fetch lifecycle milestones
      axiosInstance.get(`/admin/subscribers/${sub.user_id}/lifecycle`).then(r => {
        if (r.data.success) {
          setLifecycle(r.data.milestones || []);
          setLifecycleTokens(r.data.total_tokens || 0);
        }
      }).catch(() => { setLifecycle([]); setLifecycleTokens(0); });
      // Fetch diagnostics + classification audit + email log
      axiosInstance.get(`/admin/subscribers/${sub.user_id}/diagnostics`).then(r => {
        if (r.data.success) setDiagnostics(r.data.diagnostics);
      }).catch(() => setDiagnostics(null));
      axiosInstance.get(`/admin/subscribers/${sub.user_id}/classification-audit`).then(r => {
        if (r.data.success) setClassAudit(r.data.audit);
      }).catch(() => setClassAudit(null));
      axiosInstance.get(`/admin/subscribers/${sub.user_id}/emails`).then(r => {
        if (r.data.success) setEmailLog(r.data.emails || []);
      }).catch(() => setEmailLog([]));
      // Fetch onboarding report status
      axiosInstance.get(`/admin/subscribers/${sub.user_id}/onboarding-report/status`).then(r => {
        setReportStatus(r.data);
      }).catch(() => setReportStatus({ exists: false }));
      // Fetch activity log
      axiosInstance.get(`/admin/subscribers/${sub.user_id}/activity-log`).then(r => {
        if (r.data.success) setActivityLog(r.data.activities || []);
      }).catch(() => setActivityLog([]));
    } catch (err) {
      console.error('Failed to fetch subscriber detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePlanChange = async (portalId: string, newPlan: string) => {
    setActionLoading(true);
    try {
      await axiosInstance.put(`/admin/subscribers/${portalId}/plan`, { plan_id: newPlan });
      setAlert({ type: 'success', message: `Plan updated to ${PLAN_LABELS[newPlan]} ($${PLAN_PRICES[newPlan]}/mo)` });
      fetchSubscribers();
      if (selected?.portal_id === portalId) {
        setSelected({ ...selected, plan_id: newPlan });
      }
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to update plan' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (portalId: string, newStatus: string) => {
    setActionLoading(true);
    try {
      await axiosInstance.put(`/admin/subscribers/${portalId}/status`, { status: newStatus });
      setAlert({ type: 'success', message: `Status updated to ${newStatus}` });
      fetchSubscribers();
      if (selected?.portal_id === portalId) {
        setSelected({ ...selected, status: newStatus });
      }
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to update status' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!selected) return;
    setActionLoading(true);
    try {
      const response = await axiosInstance.put(`/admin/subscribers/${selected.portal_id}/trial`, { days: trialDays });
      setAlert({ type: 'success', message: `Trial extended by ${trialDays} days` });
      setShowTrialExtend(false);
      fetchSubscribers();
      if (selected) {
        setSelected({ ...selected, status: 'trial', trial_ends_at: response.data.trial_ends_at });
      }
    } catch (err) {
      setAlert({ type: 'danger', message: 'Failed to extend trial' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await axiosInstance.get('/admin/subscribers/export/csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscribers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setAlert({ type: 'danger', message: 'Export failed' });
    }
  };

  const getPlanColor = (plan: string) => {
    if (plan === 'broker') return colors.gold2;
    if (plan === 'ai-pro') return colors.gold;
    if (plan === 'ai-search') return colors.blue;
    return colors.muted;
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? colors.green : status === 'trial' ? colors.orange : colors.red;
  };

  const formatDate = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleString('en-US', {
      timeZone: 'America/Phoenix', month: 'numeric', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true
    });
  };

  const formatDateShort = (date: string | null) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-US', {
      timeZone: 'America/Phoenix', month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const isTrialExpiring = (trialEnd: string | null) => {
    if (!trialEnd) return false;
    const diff = new Date(trialEnd).getTime() - Date.now();
    return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000; // 3 days
  };

  return (
    <div style={{ background: colors.navy, minHeight: '100vh', fontFamily: "'DM Sans', -apple-system, sans-serif" }}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Nav */}
      <nav style={{ background: colors.navy3, borderBottom: `2px solid ${colors.gold}`, padding: '12px 0' }}>
        <Container fluid className="px-4 d-flex justify-content-between align-items-center">
          <div>
            <a href="/" style={{ textDecoration: 'none' }}>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: colors.white }}>
                AI<span style={{ color: colors.gold2 }}>Search</span>Portal
              </span>
            </a>
            <span style={{ color: colors.muted, fontSize: '14px', marginLeft: '16px' }}>/ Subscribers</span>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <a href="/admin/subscribers" style={{ color: colors.gold2, textDecoration: 'none', fontSize: '13px', padding: '6px 12px', fontWeight: 600 }}>Subscribers</a>
            <a href="/admin/sponsors" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Sponsors</a>
            <a href="/admin/contacts" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Contacts</a>
            <a href="/admin/events" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Events</a>
            <a href="/admin/support" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Support</a>
            <a href="/broker-intelligence-v2" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Broker Intelligence</a>
            <a href="/agent-portal/dashboard" style={{ color: colors.muted, textDecoration: 'none', fontSize: '13px', padding: '6px 12px' }}>Dashboard</a>
          </div>
        </Container>
      </nav>

      {alert && (
        <div style={{ padding: '0 24px', paddingTop: '12px' }}>
          <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>{alert.message}</Alert>
        </div>
      )}

      {/* Stats Strip */}
      <div style={{ background: colors.navy2, borderBottom: `1px solid ${colors.navy3}`, padding: '16px 24px' }}>
        <div className="d-flex gap-4 align-items-center flex-wrap">
          {stats && [
            { label: 'Total', value: stats.total, icon: faUsers, color: colors.blue },
            { label: 'Agents', value: stats.total_agents || stats.hub_count + stats.search_count + stats.pro_count, icon: faRocket, color: colors.muted },
            { label: 'Brokers', value: stats.total_brokers || 0, icon: faBuilding, color: colors.gold2 },
            { label: 'Internal', value: stats.total_internal || 0, icon: faUserShield, color: colors.green },
            { label: 'Trial', value: stats.trial_count, icon: faClock, color: colors.orange },
            { label: 'MRR', value: `$${(stats.mrr || 0).toLocaleString()}`, icon: faDollarSign, color: colors.green }
          ].map((s, i) => (
            <div key={i} className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={s.icon} style={{ color: s.color, fontSize: '14px' }} />
              <span style={{ color: colors.white, fontWeight: 700, fontSize: '18px' }}>{s.value}</span>
              <span style={{ color: colors.muted, fontSize: '12px' }}>{s.label}</span>
            </div>
          ))}
          <div className="ms-auto">
            <Button size="sm" onClick={handleExportCSV} style={{ background: 'transparent', border: `1px solid ${colors.muted}`, color: colors.muted, fontSize: '12px' }}>
              <FontAwesomeIcon icon={faDownload} className="me-1" /> Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Main Split Panel */}
      <div style={{ display: 'flex', height: 'calc(100vh - 130px)' }}>

        {/* Left Panel - Subscriber List */}
        <div style={{ width: '420px', minWidth: '420px', background: colors.navy2, borderRight: `1px solid ${colors.navy3}`, display: 'flex', flexDirection: 'column' }}>
          {/* Search & Filter */}
          <div style={{ padding: '12px', borderBottom: `1px solid ${colors.navy3}` }}>
            <div style={{ position: 'relative', marginBottom: '8px' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '10px', top: '10px', color: colors.muted, fontSize: '12px' }} />
              <input
                type="text" placeholder="Search subscribers..."
                value={search} onChange={e => setSearch(e.target.value)}
                style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 8px 8px 30px', color: colors.white, fontSize: '13px', outline: 'none' }}
              />
            </div>
            <div className="d-flex gap-1 mb-1">
              {['all', 'active', 'trial', 'inactive'].map(s => (
                <button key={s} onClick={() => setFilterStatus(s)}
                  style={{ background: filterStatus === s ? colors.gold : colors.navy3, color: filterStatus === s ? colors.navy : colors.muted, border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}>
                  {s}
                </button>
              ))}
            </div>
            <div className="d-flex gap-1 mb-1">
              {['all', 'agent-hub', 'ai-search', 'ai-pro'].map(p => (
                <button key={p} onClick={() => setFilterPlan(p)}
                  style={{ background: filterPlan === p ? colors.gold : colors.navy3, color: filterPlan === p ? colors.navy : colors.muted, border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                  {p === 'all' ? 'All Plans' : PLAN_LABELS[p]}
                </button>
              ))}
            </div>
            {/* KOPS-468 — user type filter: all / agents / brokers / internal */}
            <div className="d-flex gap-1 mb-1">
              {[
                { key: 'all',      label: 'All Users' },
                { key: 'agent',    label: 'Agents' },
                { key: 'broker',   label: 'Brokers' },
                { key: 'internal', label: 'Internal' },
              ].map(t => (
                <button key={t.key} onClick={() => setFilterUserType(t.key)}
                  style={{ background: filterUserType === t.key ? colors.gold : colors.navy3, color: filterUserType === t.key ? colors.navy : colors.muted, border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                  {t.label}
                </button>
              ))}
              <button onClick={() => setShowAddUser(true)}
                style={{ marginLeft: 'auto', background: colors.gold, color: colors.navy, border: 'none', borderRadius: '4px', padding: '4px 12px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>
                + Add User
              </button>
            </div>
            {/* KOPS-465 — stacked subscription filter: slice by brokerage relationship */}
            <div className="d-flex gap-1">
              {[
                { key: 'all',          label: 'All' },
                { key: 'under_broker', label: 'Under Broker' },
                { key: 'solo',         label: 'Solo' },
              ].map(b => (
                <button key={b.key} onClick={() => setFilterBrokerage(b.key)}
                  style={{ background: filterBrokerage === b.key ? colors.gold : colors.navy3, color: filterBrokerage === b.key ? colors.navy : colors.muted, border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                  {b.label}
                </button>
              ))}
            </div>
          </div>

          {/* Subscriber List */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
            ) : subscribers.length === 0 ? (
              <div className="text-center py-5" style={{ color: colors.muted }}>
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: '32px', marginBottom: '8px' }} /><br />
                No subscribers found
              </div>
            ) : (
              subscribers.map(sub => (
                <div
                  key={sub.user_id || sub.portal_id}
                  onClick={() => selectSubscriber(sub)}
                  style={{
                    padding: '12px 16px', cursor: 'pointer', borderBottom: `1px solid ${colors.navy3}`,
                    background: (selected?.user_id || selected?.portal_id) === (sub.user_id || sub.portal_id)
                      ? colors.navy3
                      : (sub.stuck_reason ? '#f4433611' : 'transparent'),
                    borderLeft: (selected?.user_id || selected?.portal_id) === (sub.user_id || sub.portal_id)
                      ? `3px solid ${colors.gold}`
                      : (sub.stuck_reason ? '3px solid #f44336' : '3px solid transparent')
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div style={{ color: colors.white, fontWeight: 600, fontSize: '14px' }}>
                        {sub.user_first_name} {sub.user_last_name}
                        {sub.account_type && sub.account_type !== 'agent' && (
                          <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '3px', background: ACCOUNT_TYPE_COLORS[sub.account_type] || colors.muted, color: '#fff', textTransform: 'uppercase' }}>
                            {sub.account_type}
                          </span>
                        )}
                      </div>
                      <div style={{ color: colors.muted, fontSize: '12px' }}>{sub.user_email}</div>
                      {sub.subdomain && <div style={{ color: colors.muted, fontSize: '11px' }}>{sub.subdomain}.aisearchportal.com</div>}
                      {sub.brokerage_name && <div style={{ color: colors.gold, fontSize: '11px' }}>{sub.brokerage_name}</div>}
                    </div>
                    <div className="text-end">
                      <span style={{
                        fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                        background: getPlanColor(sub.plan_id) + '22', color: getPlanColor(sub.plan_id)
                      }}>
                        {PLAN_LABELS[sub.plan_id] || sub.plan_id}
                      </span>
                      <div style={{ marginTop: '4px' }}>
                        <span style={{
                          fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px',
                          color: getStatusColor(sub.status || 'inactive')
                        }}>
                          {sub.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex gap-3 mt-1" style={{ fontSize: '10px', color: colors.muted }}>
                    <span>Joined {formatDateShort(sub.created_at)}</span>
                    {sub.lead_count > 0 && <span>{sub.lead_count} leads</span>}
                    {sub.connected_emails > 0 && <span>{sub.connected_emails} email(s)</span>}
                    {isTrialExpiring(sub.trial_ends_at) && (
                      <span style={{ color: colors.red, fontWeight: 700 }}>Trial expiring!</span>
                    )}
                  </div>
                  {/* KOPS-539 Onboarding Status Pips — always visible for agents */}
                  {sub.account_type === 'agent' && (() => {
                    const pip = (label: string, ok: boolean | null, detail?: string) => {
                      const color = ok === true ? '#4caf50' : ok === false ? '#9e9e9e' : '#9e9e9e';
                      const glyph = ok === true ? '●' : '○';
                      return (
                        <span title={`${label}${detail ? ': ' + detail : ''}`} style={{ color, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          {glyph} <span style={{ color: ok === true ? colors.muted : '#666' }}>{label}</span>
                        </span>
                      );
                    };
                    const ps = sub.pipeline_status;
                    const pipelineOk = ps === 'healthy' || ps === 'needs_attention';
                    const pipelineGlyph = ps === 'healthy' ? '●' : ps === 'needs_attention' ? '▲' : ps === 'critical' || ps === 'failed' ? '✕' : '○';
                    const pipelineColor = ps === 'healthy' ? '#4caf50' : ps === 'needs_attention' ? '#ff9800' : ps === 'critical' || ps === 'failed' ? '#f44336' : '#9e9e9e';
                    const pipelineLabel = ps === 'healthy' ? 'Pipeline' : ps === 'needs_attention' ? 'Pipeline' : ps === 'critical' ? 'Pipeline' : ps === 'failed' ? 'Pipeline' : 'Pipeline';
                    return (
                      <div className="d-flex gap-3 mt-1 flex-wrap" style={{ fontSize: '10px' }}>
                        {pip('Profile', sub.profile_complete ?? null)}
                        {pip('Email', (sub.connected_emails || 0) > 0, `${sub.connected_emails || 0} connected`)}
                        <span title={`Pipeline: ${ps || 'unknown'}`} style={{ color: pipelineColor, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          {pipelineGlyph} <span style={{ color: pipelineOk ? colors.muted : '#666' }}>{pipelineLabel}</span>
                          {sub.pipeline_health_score != null && <span style={{ color: '#999', marginLeft: 2 }}>({sub.pipeline_health_score})</span>}
                        </span>
                        <span title={`${sub.classified_count || 0}/${sub.lead_count || 0} classified`} style={{ color: (sub.classified_count || 0) > 0 ? '#4caf50' : '#9e9e9e', display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          {(sub.classified_count || 0) > 0 ? '●' : '○'} <span style={{ color: colors.muted }}>Classified</span>
                          {(sub.classified_count || 0) > 0 && <span style={{ color: '#4caf50', marginLeft: 2, fontWeight: 600 }}>{sub.classified_count}</span>}
                        </span>
                      </div>
                    );
                  })()}
                  {/* Stuck-reason badge — prominent when set */}
                  {sub.stuck_reason && (
                    <div className="mt-1" style={{ fontSize: '10px' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '3px', fontWeight: 700,
                        background: '#f4433622', color: '#f44336',
                        textTransform: 'uppercase', letterSpacing: '0.5px'
                      }}>
                        ⚠ Stuck: {
                          sub.stuck_reason === 'profile_empty' ? 'Profile not set up' :
                          sub.stuck_reason === 'no_email_connected' ? 'No email connected (24h+)' :
                          sub.stuck_reason === 'pipeline_never_ran' ? 'Pipeline never ran' :
                          sub.stuck_reason === 'classification_zero' ? 'Leads not classified' :
                          sub.stuck_reason
                        }
                      </span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Right Panel - Subscriber Detail */}
        <div style={{ flex: 1, overflowY: 'auto', background: '#eef1f6' }}>
          {!selected ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: '100%', color: colors.muted }}>
              <div className="text-center">
                <FontAwesomeIcon icon={faUsers} style={{ fontSize: '48px', marginBottom: '16px', color: '#ccc' }} />
                <p style={{ fontSize: '16px', color: '#999' }}>Select a subscriber to view details</p>
              </div>
            </div>
          ) : detailLoading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: colors.gold }} /></div>
          ) : (
            <div style={{ padding: '24px' }}>
              {/* Profile Header */}
              <div style={{ background: '#f8f9fa', borderRadius: '12px', padding: '24px', marginBottom: '20px', borderLeft: `4px solid ${colors.gold}` }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h3 style={{ color: colors.navy, fontFamily: "'Bebas Neue', sans-serif", fontSize: '32px', letterSpacing: '1px', margin: 0 }}>
                      {selected.user_first_name} {selected.user_last_name}
                    </h3>
                    <div className="d-flex gap-3 mt-2 flex-wrap" style={{ fontSize: '14px' }}>
                      <span style={{ color: '#555' }}><FontAwesomeIcon icon={faEnvelope} className="me-1" style={{ color: colors.gold }} />{selected.user_email}</span>
                      {selected.user_phone && (
                        <>
                          <span style={{ color: '#555' }}><FontAwesomeIcon icon={faPhone} className="me-1" style={{ color: colors.gold }} />{selected.user_phone}</span>
                          <Button size="sm" variant="outline-success" style={{ fontSize: '11px', padding: '2px 8px', borderColor: '#27ae60', color: '#27ae60' }}
                            onClick={() => {
                              if (teamCallers.length === 0) {
                                axiosInstance.get('/contacts/team-callers').then(r => {
                                  if (r.data.success) setTeamCallers(r.data.callers || []);
                                });
                              }
                              setShowCallPanel(!showCallPanel);
                            }}>
                            <FontAwesomeIcon icon={faPhone} className="me-1" /> Call
                          </Button>
                        </>
                      )}
                      <span style={{ color: '#555' }}><FontAwesomeIcon icon={faGlobe} className="me-1" style={{ color: colors.gold }} />{selected.subdomain}.aisearchportal.com</span>
                      {/* MLS Status Badge */}
                      {(detail as any)?.mlsStatus?.activated ? (
                        <span style={{ background: '#27ae60', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                          MLS Active — {(detail as any).mlsStatus.feed_type === 'vendor' ? 'Vendor Feed' : (detail as any).mlsStatus.feed_type}
                        </span>
                      ) : (
                        <span style={{ background: '#f39c12', color: '#fff', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 }}>
                          MLS — $150 Not Paid
                        </span>
                      )}
                    </div>

                    {/* Call Panel */}
                    {showCallPanel && selected?.user_phone && (
                      <div style={{ background: '#f0faf0', borderRadius: '8px', padding: '12px', marginTop: '10px', border: '1px solid #c8e6c9' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, marginBottom: '8px' }}>
                          <FontAwesomeIcon icon={faPhone} className="me-1" style={{ color: '#27ae60' }} /> Call {selected.user_first_name} {selected.user_last_name} via Conference Bridge
                        </div>
                        <div className="d-flex gap-2 align-items-end">
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '10px', color: '#666' }}>Who is calling?</label>
                            <Form.Select size="sm" value={selectedCaller} onChange={e => setSelectedCaller(e.target.value)} style={{ fontSize: '12px' }}>
                              <option value="">Select team member...</option>
                              {teamCallers.map((c: any) => (
                                <option key={c.id} value={c.user_phone}>
                                  {c.user_first_name} {c.user_last_name} — {c.user_phone}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                          <Button size="sm" disabled={!selectedCaller || calling}
                            onClick={async () => {
                              setCalling(true);
                              try {
                                const caller = teamCallers.find((c: any) => c.user_phone === selectedCaller);
                                const res = await axiosInstance.post(`/contacts/${selected.user_id}/call`, {
                                  callerPhone: selectedCaller,
                                  callerName: caller ? `${caller.user_first_name} ${caller.user_last_name}` : 'Team Member'
                                });
                                if (res.data.success) {
                                  setAlert({ type: 'success', message: `Calling ${selectedCaller} first, then connecting to ${selected.user_first_name}...` });
                                  setShowCallPanel(false);
                                }
                              } catch (err: any) {
                                setAlert({ type: 'danger', message: err.response?.data?.error || 'Call failed' });
                              }
                              setCalling(false);
                            }}
                            style={{ background: '#27ae60', border: 'none', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {calling ? 'Connecting...' : '📞 Call Now'}
                          </Button>
                        </div>
                        <div style={{ fontSize: '10px', color: '#888', marginTop: '6px' }}>
                          Your phone will ring first from (520) 251-9595. Answer to connect to {selected.user_first_name}. Call is recorded.
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="text-end">
                    <div style={{ color: getPlanColor(selected.plan_id), fontWeight: 700, fontSize: '24px', fontFamily: "'Bebas Neue', sans-serif" }}>
                      ${PLAN_PRICES[selected.plan_id] || 0}/mo
                    </div>
                    <div className="d-flex gap-2 mt-1">
                      <Form.Select size="sm" value={selected.plan_id} onChange={e => handlePlanChange(selected.portal_id, e.target.value)}
                        style={{ background: 'white', color: colors.navy, border: '1px solid #ddd', fontSize: '12px', width: '130px' }}>
                        <option value="agent-hub">Agent Hub</option>
                        <option value="ai-search">AI Search</option>
                        <option value="ai-pro">AI Pro</option>
                      </Form.Select>
                      <Form.Select size="sm" value={selected.status || 'inactive'} onChange={e => handleStatusChange(selected.portal_id, e.target.value)}
                        style={{ background: 'white', color: colors.navy, border: '1px solid #ddd', fontSize: '12px', width: '100px' }}>
                        <option value="active">Active</option>
                        <option value="trial">Trial</option>
                        <option value="inactive">Inactive</option>
                      </Form.Select>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="d-flex gap-2 mt-3 flex-wrap">
                  <Button size="sm"
                    style={{ background: 'linear-gradient(135deg, #3498db, #2980b9)', border: 'none', fontWeight: 600, fontSize: '12px', color: '#fff' }}
                    onClick={async () => {
                      setShowSupportChat(true);
                      setSupportLoading(true);
                      setSupportMessages([]);
                      try {
                        const res = await axiosInstance.get(`/chat/support/conversations/${selected.user_id}`);
                        if (res.data.success) {
                          setSupportConvId(res.data.conversation.id);
                          setSupportMessages(res.data.messages || []);
                        }
                      } catch (err) {
                        console.error('Failed to load support chat:', err);
                      }
                      setSupportLoading(false);
                    }}>
                    <FontAwesomeIcon icon={faComments} className="me-1" /> Support
                  </Button>
                  <Button size="sm" onClick={() => setShowTrialExtend(!showTrialExtend)}
                    style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600, fontSize: '12px' }}>
                    <FontAwesomeIcon icon={faCalendarPlus} className="me-1" /> Extend Trial
                  </Button>
                  <Button size="sm"
                    style={{ background: 'linear-gradient(135deg, #27ae60, #2ecc71)', border: 'none', fontWeight: 600, fontSize: '12px', color: '#fff' }}
                    onClick={async () => {
                      try {
                        setActionLoading(true);
                        const res = await axiosInstance.post('/portal-settings/enrich-from-mls', {}, {
                          headers: { 'x-admin-enrich-user': selected.user_id }
                        });
                        if (res.data.success) {
                          setAlert({ type: 'success', message: `MLS enriched: ${res.data.message}` });
                          await fetchSubscribers();
                          selectSubscriber({ ...selected });
                        } else {
                          setAlert({ type: 'warning', message: res.data.message || 'No MLS match found' });
                        }
                      } catch (err: any) {
                        setAlert({ type: 'danger', message: err?.response?.data?.message || 'MLS enrichment failed' });
                      } finally {
                        setActionLoading(false);
                      }
                    }}
                    disabled={actionLoading}>
                    <FontAwesomeIcon icon={faDatabase} className="me-1" /> Enrich from MLS
                  </Button>
                  <Button size="sm" variant="outline-secondary" style={{ fontSize: '12px' }}
                    onClick={() => window.open(`https://${selected.subdomain}.aisearchportal.com`, '_blank')}>
                    <FontAwesomeIcon icon={faExternalLinkAlt} className="me-1" /> View Portal
                  </Button>
                  {selected.stripe_customer_id && (
                    <Button size="sm" variant="outline-secondary" style={{ fontSize: '12px' }}
                      onClick={() => window.open(`https://dashboard.stripe.com/customers/${selected.stripe_customer_id}`, '_blank')}>
                      <FontAwesomeIcon icon={faDollarSign} className="me-1" /> Stripe
                    </Button>
                  )}
                  {/* Disable / Enable Account */}
                  {selected.status === 'active' || selected.status === 'trial' ? (
                    <Button size="sm" variant="outline-danger" style={{ fontSize: '12px' }}
                      disabled={actionLoading}
                      onClick={async () => {
                        if (!window.confirm(`Disable account for ${selected.user_first_name} ${selected.user_last_name}? They will lose access immediately.`)) return;
                        setActionLoading(true);
                        try {
                          const disRes = await axiosInstance.put(`/admin/subscribers/${selected.portal_id}/status`, { status: 'inactive' });
                          setAlert({ type: 'success', message: disRes.data?.message || `Account disabled for ${selected.user_first_name}` });
                          await fetchSubscribers();
                          selectSubscriber({ ...selected, status: 'inactive' });
                        } catch (err: any) {
                          setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to disable account' });
                        } finally { setActionLoading(false); }
                      }}>
                      <FontAwesomeIcon icon={faPowerOff} className="me-1" /> Disable Account
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline-success" style={{ fontSize: '12px' }}
                      disabled={actionLoading}
                      onClick={async () => {
                        setActionLoading(true);
                        try {
                          const enRes = await axiosInstance.put(`/admin/subscribers/${selected.portal_id}/status`, { status: 'active' });
                          setAlert({ type: 'success', message: enRes.data?.message || `Account re-enabled for ${selected.user_first_name}` });
                          await fetchSubscribers();
                          selectSubscriber({ ...selected, status: 'active' });
                        } catch (err: any) {
                          setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to enable account' });
                        } finally { setActionLoading(false); }
                      }}>
                      <FontAwesomeIcon icon={faCheckCircle} className="me-1" /> Enable Account
                    </Button>
                  )}
                </div>
              </div>

              {/* Extend Trial Form */}
              {showTrialExtend && (
                <div style={{ background: colors.navy2, borderRadius: '12px', padding: '20px', marginBottom: '20px', border: `1px solid ${colors.gold}` }}>
                  <h6 style={{ color: colors.gold2, fontSize: '13px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Extend Trial for {selected.user_first_name}
                  </h6>
                  {selected.trial_ends_at && (
                    <p style={{ color: colors.muted, fontSize: '12px', margin: '0 0 12px' }}>
                      Current trial ends: {formatDate(selected.trial_ends_at)}
                    </p>
                  )}
                  <div className="d-flex gap-2 align-items-center">
                    <Form.Select size="sm" value={trialDays} onChange={e => setTrialDays(Number(e.target.value))}
                      style={{ background: colors.navy3, border: 'none', color: colors.white, fontSize: '13px', width: '140px' }}>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={60}>60 days</option>
                    </Form.Select>
                    <Button size="sm" onClick={handleExtendTrial} disabled={actionLoading}
                      style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontWeight: 600 }}>
                      {actionLoading ? <Spinner size="sm" /> : 'Extend'}
                    </Button>
                    <Button size="sm" variant="outline-light" onClick={() => setShowTrialExtend(false)}
                      style={{ borderColor: colors.muted, color: colors.muted }}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* KOPS-620: Account / Operations tab bar — internal employees only */}
              <div style={{ display: 'flex', gap: '0', marginBottom: '16px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #1E3557' }}>
                <button
                  onClick={() => setAdminTab('account')}
                  style={{
                    flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '12px',
                    letterSpacing: '1px', textTransform: 'uppercase',
                    background: adminTab === 'account' ? '#0F1E35' : '#f8f9fa',
                    color: adminTab === 'account' ? '#C9952A' : '#666',
                  }}
                >
                  Account
                </button>
                <button
                  onClick={async () => {
                    setAdminTab('operations');
                    if (!opsData && selected) {
                      setOpsLoading(true);
                      try {
                        const res = await axiosInstance.get(`/admin/subscribers/${selected.portal_id}/operations`);
                        setOpsData(res.data?.data || null);
                      } catch { /* best effort */ }
                      setOpsLoading(false);
                    }
                  }}
                  style={{
                    flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '12px',
                    letterSpacing: '1px', textTransform: 'uppercase',
                    background: adminTab === 'operations' ? '#0F1E35' : '#f8f9fa',
                    color: adminTab === 'operations' ? '#C9952A' : '#666',
                  }}
                >
                  Operations
                </button>
              </div>

              {/* Operations Tab Content */}
              {adminTab === 'operations' && (
                <div style={{ marginBottom: '20px' }}>
                  {opsLoading ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" style={{ color: '#C9952A' }} /></div>
                  ) : opsData ? (
                    <>
                      {/* AI Token Cost Breakdown */}
                      <div style={{ background: '#162844', borderRadius: '12px', padding: '18px 20px', marginBottom: '16px', border: '1px solid #1E3557', borderLeft: `4px solid ${opsData.token_costs?.over_target ? '#e74c3c' : '#27ae60'}` }}>
                        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '12px' }}>
                          <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
                            AI Token Costs
                          </h6>
                          <span style={{ fontSize: '20px', fontWeight: 700, color: opsData.token_costs?.over_target ? '#e74c3c' : '#27ae60' }}>
                            ${opsData.token_costs?.total_cost?.toFixed(2) || '0.00'}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between py-1 mb-2" style={{ fontSize: '12px', color: '#8FA3BF' }}>
                          <span>Total API Calls: {opsData.token_costs?.total_calls?.toLocaleString() || 0}</span>
                          <span>Target: {opsData.token_costs?.target_cost}</span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#E8EEF6' }}>
                          {opsData.token_costs?.breakdown?.map((row: any, i: number) => (
                            <div key={i} className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
                              <div>
                                <span style={{ color: row.operation_type ? '#E8EEF6' : '#e74c3c', fontWeight: row.operation_type ? 400 : 700 }}>
                                  {row.operation_type || '(unlabeled)'}
                                </span>
                                {row.processing_mode && row.processing_mode !== 'full_processing' && (
                                  <span style={{ color: '#8FA3BF', fontSize: '10px', marginLeft: '6px' }}>{row.processing_mode}</span>
                                )}
                              </div>
                              <div style={{ textAlign: 'right' }}>
                                <span style={{ fontWeight: 600 }}>${parseFloat(row.cost).toFixed(2)}</span>
                                <span style={{ color: '#8FA3BF', marginLeft: '8px', fontSize: '11px' }}>{parseInt(row.calls).toLocaleString()} calls</span>
                                {parseInt(row.failed) > 0 && (
                                  <span style={{ color: '#e74c3c', marginLeft: '6px', fontSize: '10px' }}>{row.failed} failed</span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Customer Journey Stages */}
                      <div style={{ background: '#162844', borderRadius: '12px', padding: '18px 20px', marginBottom: '16px', border: '1px solid #1E3557', borderLeft: '4px solid #3498db' }}>
                        <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 12px 0' }}>
                          Customer Journey
                        </h6>
                        <div style={{ fontSize: '12px' }}>
                          {opsData.journey?.map((stage: any, i: number) => {
                            const meta = stage.metadata || {};
                            const detail = stage.milestone === 'classification' ? `${meta.total || 0} leads — ${Object.entries(meta.classifications || {}).map(([k,v]) => `${v} ${k.replace(/_/g,' ')}`).join(', ')}`
                              : stage.milestone === 'email_sync' ? `${meta.emails_synced || 0} emails synced`
                              : stage.milestone === 'leads_discovered' ? `${meta.contacts_discovered || 0} contacts, ${meta.emails_scanned || 0} emails scanned`
                              : stage.milestone === 'profiles_built' ? `${meta.profiles_created || 0} profiles, ${meta.errors || 0} errors`
                              : stage.milestone === 'email_connected' ? `${meta.provider || ''} — ${meta.email || ''}`
                              : '';
                            return (
                              <div key={i} className="d-flex align-items-start gap-2 py-1" style={{ borderBottom: '1px solid #1E3557' }}>
                                <span style={{
                                  fontSize: '10px', padding: '2px 6px', borderRadius: '3px', marginTop: '2px', whiteSpace: 'nowrap',
                                  background: stage.status === 'complete' ? 'rgba(39,174,96,0.15)' : stage.status === 'failed' ? 'rgba(231,76,60,0.15)' : 'rgba(52,152,219,0.15)',
                                  color: stage.status === 'complete' ? '#27ae60' : stage.status === 'failed' ? '#e74c3c' : '#3498db',
                                  fontWeight: 700,
                                }}>
                                  {stage.status === 'complete' ? 'DONE' : stage.status?.toUpperCase() || 'PENDING'}
                                </span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ color: '#E8EEF6', fontWeight: 600 }}>{(stage.milestone || '').replace(/_/g, ' ')}</div>
                                  {detail && <div style={{ color: '#8FA3BF', fontSize: '11px', marginTop: '2px' }}>{detail}</div>}
                                  {stage.error_message && <div style={{ color: '#e74c3c', fontSize: '11px', marginTop: '2px' }}>{stage.error_message}</div>}
                                </div>
                                <span style={{ color: '#8FA3BF', fontSize: '10px', whiteSpace: 'nowrap' }}>
                                  {stage.completed_at ? new Date(stage.completed_at).toLocaleString() : ''}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Lead Pipeline */}
                      <div style={{ background: '#162844', borderRadius: '12px', padding: '18px 20px', marginBottom: '16px', border: '1px solid #1E3557', borderLeft: '4px solid #9b59b6' }}>
                        <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 12px 0' }}>
                          Lead Pipeline
                        </h6>
                        <div className="d-flex gap-3 mb-3" style={{ fontSize: '13px' }}>
                          <div style={{ textAlign: 'center', flex: 1, background: '#1E3557', borderRadius: '8px', padding: '10px' }}>
                            <div style={{ fontSize: '22px', fontWeight: 700, color: '#E8EEF6' }}>{parseInt(opsData.leads?.total_leads || 0).toLocaleString()}</div>
                            <div style={{ color: '#8FA3BF', fontSize: '10px', textTransform: 'uppercase' }}>Total Leads</div>
                          </div>
                          <div style={{ textAlign: 'center', flex: 1, background: '#1E3557', borderRadius: '8px', padding: '10px' }}>
                            <div style={{ fontSize: '22px', fontWeight: 700, color: '#27ae60' }}>{parseInt(opsData.leads?.classified || 0).toLocaleString()}</div>
                            <div style={{ color: '#8FA3BF', fontSize: '10px', textTransform: 'uppercase' }}>Classified</div>
                          </div>
                          <div style={{ textAlign: 'center', flex: 1, background: '#1E3557', borderRadius: '8px', padding: '10px' }}>
                            <div style={{ fontSize: '22px', fontWeight: 700, color: '#3498db' }}>{parseInt(opsData.leads?.coached || 0).toLocaleString()}</div>
                            <div style={{ color: '#8FA3BF', fontSize: '10px', textTransform: 'uppercase' }}>Coached</div>
                          </div>
                        </div>
                        {opsData.leads?.classifications?.length > 0 && (
                          <div style={{ fontSize: '12px' }}>
                            {opsData.leads.classifications.map((c: any, i: number) => (
                              <div key={i} className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
                                <span style={{ color: '#E8EEF6' }}>{(c.lead_classification || '').replace(/_/g, ' ')}</span>
                                <span style={{ color: '#C9952A', fontWeight: 600 }}>{parseInt(c.count).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* MLS Status */}
                      <div style={{ background: '#162844', borderRadius: '12px', padding: '18px 20px', marginBottom: '16px', border: '1px solid #1E3557', borderLeft: `4px solid ${opsData.mls?.mls_activation_paid ? '#27ae60' : '#8FA3BF'}` }}>
                        <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: '0 0 12px 0' }}>
                          MLS Access
                        </h6>
                        <div style={{ fontSize: '13px', color: '#E8EEF6' }}>
                          <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
                            <span style={{ color: '#8FA3BF' }}>Feed Type</span>
                            <span>{opsData.mls?.mls_feed_type || 'pending'}</span>
                          </div>
                          <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
                            <span style={{ color: '#8FA3BF' }}>Activation Paid</span>
                            <span style={{ color: opsData.mls?.mls_activation_paid ? '#27ae60' : '#e74c3c' }}>
                              {opsData.mls?.mls_activation_paid ? 'Yes' : 'No'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#8FA3BF' }}>No operations data available</div>
                  )}
                </div>
              )}

              {adminTab === 'account' && <>
              {/* KOPS-465 — Brokerage Relationship card (stacked subscription layer) */}
              <BrokerageRelationshipCard
                relationship={detail?.brokerageRelationship || null}
                individualPlan={selected.plan_id}
                userId={selected.user_id}
              />

              {/* KOPS-465 — ARMLS credentials admin card. Stores the key in
                  AWS Secrets Manager under /kairosiq/customers/{user_id}/integrations/mls/armls-credentials.
                  For broker admins, the key is inherited by all agents under their
                  brokerage via mls-credential-router.service.js fallback logic. */}
              <MLSCredentialsCard subscriberId={String(selected.portal_id)} />

              {/* KOPS-542: County Property Intelligence */}
              <div style={{ background: '#162844', borderRadius: '10px', padding: '16px', marginBottom: '16px', border: '1px solid #1E3557' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h6 style={{ color: 'white', fontWeight: 700, margin: 0, fontSize: '13px' }}>
                    <FontAwesomeIcon icon={faHome} className="me-2" style={{ color: colors.gold }} />
                    Property Intelligence
                  </h6>
                  <Button
                    size="sm"
                    style={{ background: colors.gold, border: 'none', fontSize: '11px', fontWeight: 600, color: '#0F1E35' }}
                    onClick={async () => {
                      try {
                        const btn = document.getElementById('admin-prop-scan') as HTMLButtonElement;
                        if (btn) { btn.disabled = true; btn.textContent = 'Scanning...'; }
                        const userId = selected.user_id;
                        const { data } = await axiosInstance.post('/ai-coach/scan-properties', {}, {
                          headers: { 'x-user-id': userId }
                        });
                        setAlert({ type: 'success', message: `Property scan: ${data.leadsScanned || 0} leads scanned, ${data.propertiesFound || 0} properties found, ${data.changesDetected || 0} changes detected` });
                        if (btn) { btn.disabled = false; btn.textContent = 'Run Scan'; }
                      } catch (err) {
                        setAlert({ type: 'danger', message: 'Scan failed — check server logs' });
                        const btn = document.getElementById('admin-prop-scan') as HTMLButtonElement;
                        if (btn) { btn.disabled = false; btn.textContent = 'Run Scan'; }
                      }
                    }}
                    id="admin-prop-scan"
                  >
                    Run Scan
                  </Button>
                </div>
                <div style={{ fontSize: '11px', color: '#8FA3BF', lineHeight: '1.6' }}>
                  Scans county records for this subscriber's past buyers, sellers, and sphere contacts.
                  Finds owned properties, detects foreclosures, absentee owners, and title changes.
                  Runs automatically on the 1st of every month.
                </div>
              </div>

              {/* KOPS-539: Onboarding Workflow — step-by-step with manual triggers */}
              {selected && (() => {
                const getMilestone = (name: string) => lifecycle.find((m: any) => m.milestone === name);
                const isComplete = (name: string) => getMilestone(name)?.status === 'complete';
                const profileDone = !!(detail?.subscriber?.brand_color && detail?.subscriber?.brokerage_name) || isComplete('profile_setup');
                const idxSent = emailLog.some((e: any) => e.template === 'broker_idx');
                const emailConnected = (detail?.connectedEmails?.length || 0) > 0;
                const leadsFound = (selected.lead_count || 0) > 0 || isComplete('leads_discovered');
                const classified = (selected.classified_count || 0) > 0 || isComplete('classification');
                const coachActive = isComplete('ai_coach_active') || (detail?.coachActivity || 0) > 0;

                const steps = [
                  { key: 'profile', label: 'Profile Setup', done: profileDone, detail: profileDone ? `${detail?.subscriber?.brokerage_name || 'Set'} · ${(detail?.subscriber as any)?.license_number || ''}` : 'Not enriched — click to auto-fill from MLS', action: 'enrich', actionLabel: 'Enrich from MLS' },
                  { key: 'idx', label: 'Broker IDX Authorization', done: idxSent, detail: idxSent ? 'IDX email sent to broker' : 'Broker needs to authorize MLS access', action: 'broker_idx', actionLabel: 'Send IDX Email' },
                  { key: 'email', label: 'Connect Email', done: emailConnected, detail: emailConnected ? `${detail?.connectedEmails?.map((e: any) => e.email).join(', ')}` : 'Agent needs to connect Gmail/Outlook', action: 'connect_email', actionLabel: 'Send Reminder' },
                  { key: 'leads', label: 'Lead Discovery', done: leadsFound, detail: leadsFound ? `${selected.lead_count || 0} leads discovered` : 'Pipeline needs to run', action: 'pipeline', actionLabel: 'Run Pipeline' },
                  { key: 'classify', label: 'Classification', done: classified, detail: classified ? `${selected.classified_count || 0} classified` : 'Waiting for lead discovery', action: 'classify', actionLabel: 'Run Classification' },
                  { key: 'coach', label: 'AI Coach Active', done: coachActive, detail: coachActive ? `${detail?.coachActivity || 0} actions generated` : 'Activates after classification', action: null, actionLabel: '' },
                ];
                const completedCount = steps.filter(s => s.done).length;
                const pct = Math.round((completedCount / steps.length) * 100);

                return (
                  <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: `2px solid ${colors.gold}` }}>
                    <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '12px' }}>
                      <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
                        <FontAwesomeIcon icon={faRocket} className="me-2" />Onboarding Workflow
                      </h6>
                      <span style={{ fontSize: '11px', color: completedCount === steps.length ? '#2e7d32' : colors.gold, fontWeight: 700 }}>
                        {completedCount}/{steps.length} complete
                      </span>
                    </div>

                    {/* Progress bar */}
                    <div style={{ background: '#e9ecef', borderRadius: 4, height: 6, marginBottom: 16 }}>
                      <div style={{ background: `linear-gradient(90deg, ${colors.gold}, ${colors.gold2})`, borderRadius: 4, height: 6, width: `${pct}%`, transition: 'width 0.3s' }} />
                    </div>

                    {/* Steps */}
                    {steps.map((step, i) => (
                      <div key={step.key} style={{
                        display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 0',
                        borderBottom: i < steps.length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}>
                        {/* Status icon + connector */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 24 }}>
                          <span style={{
                            width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: 11, fontWeight: 700,
                            background: step.done ? '#e8f5e9' : '#f5f5f5',
                            color: step.done ? '#2e7d32' : '#999',
                            border: `2px solid ${step.done ? '#4caf50' : '#ddd'}`,
                          }}>
                            {step.done ? '✓' : (i + 1)}
                          </span>
                          {i < steps.length - 1 && (
                            <div style={{ width: 2, height: 16, background: step.done ? '#4caf5044' : '#e0e0e0', marginTop: 2 }} />
                          )}
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: step.done ? '#333' : '#666' }}>
                            {step.label}
                          </div>
                          <div style={{ fontSize: 11, color: step.done ? '#4caf50' : '#999', marginTop: 2 }}>
                            {step.detail}
                          </div>
                        </div>

                        {/* Action button */}
                        {step.action && !step.done && (
                          <Button size="sm" variant="outline-warning"
                            style={{ fontSize: 10, padding: '2px 8px', whiteSpace: 'nowrap', borderColor: colors.gold, color: colors.gold }}
                            disabled={actionLoading}
                            onClick={async () => {
                              setActionLoading(true);
                              try {
                                if (step.action === 'enrich') {
                                  await axiosInstance.post('/portal-settings/enrich-from-mls', {}, { headers: { 'x-admin-enrich-user': selected.user_id } });
                                  setAlert({ type: 'success', message: 'MLS enrichment complete' });
                                } else if (step.action === 'pipeline') {
                                  await axiosInstance.post(`/admin/subscribers/${selected.user_id}/pipeline-retry`);
                                  setAlert({ type: 'success', message: 'Pipeline triggered' });
                                } else if (step.action === 'classify') {
                                  await axiosInstance.post(`/admin/subscribers/${selected.user_id}/run-classification`, { force: false });
                                  setAlert({ type: 'success', message: 'Classification started' });
                                } else {
                                  await axiosInstance.post(`/admin/subscribers/${selected.user_id}/send-onboarding-email`, { template: step.action });
                                  setAlert({ type: 'success', message: `${step.actionLabel} sent` });
                                }
                                // Refresh data
                                await fetchSubscribers();
                                selectSubscriber({ ...selected });
                              } catch (err: any) {
                                setAlert({ type: 'danger', message: err.response?.data?.error || `Failed: ${step.actionLabel}` });
                              } finally { setActionLoading(false); }
                            }}
                          >
                            {step.actionLabel}
                          </Button>
                        )}
                        {step.done && (
                          <span style={{ fontSize: 10, color: '#4caf50', fontWeight: 600 }}>✓ Done</span>
                        )}
                      </div>
                    ))}

                    {/* Download Onboarding Report — visible once classification is done */}
                    {classified && (
                      <div style={{ marginTop: 12, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
                        <div style={{ padding: '10px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: colors.navy }}>
                              <FontAwesomeIcon icon={faDownload} className="me-1" style={{ color: colors.gold }} />
                              AI Onboarding Report
                            </div>
                            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                              {reportStatus.exists && reportStatus.report
                                ? `Generated ${new Date(reportStatus.report.generated_at).toLocaleDateString()}`
                                : 'Report will be generated on download'}
                            </div>
                          </div>
                          <div className="d-flex gap-2">
                            <Button size="sm"
                              style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', color: colors.navy, fontWeight: 700, fontSize: 11, padding: '5px 14px' }}
                              disabled={reportLoading}
                              onClick={async () => {
                                setReportLoading(true);
                                try {
                                  const response = await axiosInstance.get(
                                    `/admin/subscribers/${selected.user_id}/onboarding-report`,
                                    { responseType: 'blob' }
                                  );
                                  const url = window.URL.createObjectURL(new Blob([response.data]));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', reportStatus.report?.file_name || `KairosIQ_Onboarding_Report.docx`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  window.URL.revokeObjectURL(url);
                                  const statusRes = await axiosInstance.get(`/admin/subscribers/${selected.user_id}/onboarding-report/status`);
                                  setReportStatus(statusRes.data);
                                } catch (err: any) {
                                  setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to download report' });
                                } finally { setReportLoading(false); }
                              }}
                            >
                              {reportLoading ? <Spinner animation="border" size="sm" /> : 'Download'}
                            </Button>
                            <Button size="sm" variant="outline-primary"
                              style={{ fontSize: 11, padding: '5px 10px' }}
                              disabled={reportEmailLoading}
                              onClick={async () => {
                                if (showReportEmail) { setShowReportEmail(false); return; }
                                setReportEmailLoading(true);
                                try {
                                  const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/jasper-draft`, {
                                    task: `Send this customer their AI Onboarding Report results email. Their book of business has been classified: ${selected.classified_count || 0} leads classified, ${selected.lead_count || 0} total leads. Use a warm, professional tone. Highlight what we found (active prospects, past clients, sphere of influence) and tell them their full report is attached / available on their dashboard. Encourage them to log in and review their classified book. This is a milestone — their data is organized and ready to work.`,
                                    channel: 'email',
                                    subscriberName: `${selected.user_first_name} ${selected.user_last_name}`,
                                    subscriberEmail: selected.user_email,
                                  });
                                  if (res.data.success) {
                                    setReportEmailDraft(res.data.draft);
                                    setReportEmailSubject(res.data.subject || `Your AI Onboarding Report is Ready — ${selected.classified_count || 0} Contacts Classified`);
                                    setShowReportEmail(true);
                                  }
                                } catch (err: any) {
                                  setAlert({ type: 'danger', message: 'Failed to generate email draft' });
                                } finally { setReportEmailLoading(false); }
                              }}
                            >
                              {reportEmailLoading ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faPaperPlane} className="me-1" />Email</>}
                            </Button>
                            {reportStatus.exists && (
                              <>
                                <Button size="sm" variant="outline-secondary"
                                  style={{ fontSize: 11, padding: '5px 10px' }}
                                  disabled={reportLoading}
                                  onClick={async () => {
                                    setReportLoading(true);
                                    try {
                                      const response = await axiosInstance.get(
                                        `/admin/subscribers/${selected.user_id}/onboarding-report?regenerate=true`,
                                        { responseType: 'blob' }
                                      );
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement('a');
                                      link.href = url;
                                      link.setAttribute('download', reportStatus.report?.file_name || `KairosIQ_Onboarding_Report.docx`);
                                      document.body.appendChild(link);
                                      link.click();
                                      link.remove();
                                      window.URL.revokeObjectURL(url);
                                      const statusRes = await axiosInstance.get(`/admin/subscribers/${selected.user_id}/onboarding-report/status`);
                                      setReportStatus(statusRes.data);
                                      setReportPublished(false);
                                    } catch (err: any) {
                                      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to regenerate report' });
                                    } finally { setReportLoading(false); }
                                  }}
                                >
                                  Regenerate
                                </Button>
                                <Button size="sm"
                                  style={{
                                    fontSize: 11, padding: '5px 12px', fontWeight: 700,
                                    background: reportPublished ? '#27ae60' : colors.navy,
                                    border: 'none', color: '#fff',
                                  }}
                                  disabled={publishingReport}
                                  onClick={async () => {
                                    setPublishingReport(true);
                                    try {
                                      const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/onboarding-report/publish`);
                                      if (res.data.success) {
                                        setReportPublished(true);
                                        setAlert({ type: 'success', message: `Report published to ${selected.user_first_name}'s portal` });
                                      }
                                    } catch (err: any) {
                                      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to publish report' });
                                    } finally { setPublishingReport(false); }
                                  }}
                                >
                                  {publishingReport ? <Spinner animation="border" size="sm" /> : reportPublished ? <><FontAwesomeIcon icon={faCheckCircle} className="me-1" />Published</> : <><FontAwesomeIcon icon={faRocket} className="me-1" />Publish to Portal</>}
                                </Button>
                                <Button size="sm"
                                  style={{ fontSize: 11, padding: '5px 12px', fontWeight: 700, background: '#C9A84C', border: 'none', color: '#0F1E35' }}
                                  disabled={publishingOpp}
                                  onClick={async () => {
                                    setPublishingOpp(true);
                                    try {
                                      const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/opportunity-report/publish`);
                                      if (res.data.success) {
                                        setAlert({ type: 'success', message: `Opportunity Report published to ${selected.user_first_name}'s portal (${(res.data.tier1 || 0) + (res.data.tier2 || 0)} contacts)` });
                                      }
                                    } catch (err: any) {
                                      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to publish Opportunity Report' });
                                    } finally { setPublishingOpp(false); }
                                  }}
                                >
                                  {publishingOpp ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faRocket} className="me-1" />Publish Opportunity Report</>}
                                </Button>
                                <Button size="sm"
                                  style={{ fontSize: 11, padding: '5px 12px', fontWeight: 700, background: '#0F1E35', border: '1px solid #C9A84C', color: '#C9A84C' }}
                                  disabled={sendingOppEmail}
                                  onClick={async () => {
                                    setSendingOppEmail(true);
                                    try {
                                      const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/send-onboarding-email`, { template: 'opportunity_ready' });
                                      if (res.data.success) {
                                        setAlert({ type: 'success', message: `Opportunity email sent to ${selected.user_first_name}` });
                                      }
                                    } catch (err: any) {
                                      setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send opportunity email' });
                                    } finally { setSendingOppEmail(false); }
                                  }}
                                >
                                  {sendingOppEmail ? <Spinner animation="border" size="sm" /> : <><FontAwesomeIcon icon={faRocket} className="me-1" />Send Opportunity Email</>}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Email draft panel — inline compose */}
                        {showReportEmail && (
                          <div style={{ padding: '12px', borderTop: '1px solid #e0e0e0', background: '#f0f7ff' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: colors.navy, textTransform: 'uppercase', marginBottom: 6 }}>
                              <FontAwesomeIcon icon={faPaperPlane} className="me-1" style={{ color: colors.gold }} />
                              Email Report Summary to {selected.user_first_name}
                            </div>
                            <input
                              value={reportEmailSubject}
                              onChange={e => setReportEmailSubject(e.target.value)}
                              placeholder="Subject"
                              style={{ width: '100%', fontSize: 13, border: '1px solid #ddd', borderRadius: 6, padding: '8px 10px', marginBottom: 6, fontWeight: 600 }}
                            />
                            <textarea
                              value={reportEmailDraft}
                              onChange={e => setReportEmailDraft(e.target.value)}
                              rows={8}
                              style={{ width: '100%', fontSize: 13, border: '1px solid #ddd', borderRadius: 6, padding: '10px', resize: 'vertical', lineHeight: 1.6 }}
                            />
                            <div style={{ fontSize: 10, color: '#888', marginTop: 4, marginBottom: 8 }}>
                              AI-generated draft based on report data. Edit as needed before sending.
                            </div>
                            <div className="d-flex gap-2">
                              <Button size="sm"
                                disabled={reportEmailSending || !reportEmailDraft || !reportEmailSubject}
                                onClick={async () => {
                                  setReportEmailSending(true);
                                  try {
                                    const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/jasper-send`, {
                                      message: reportEmailDraft,
                                      subject: reportEmailSubject,
                                      channel: 'email',
                                      task: 'Send AI Onboarding Report summary email',
                                    });
                                    if (res.data.success) {
                                      setAlert({ type: 'success', message: `Report email sent to ${selected.user_email}` });
                                      setShowReportEmail(false);
                                      setReportEmailDraft('');
                                      setReportEmailSubject('');
                                    }
                                  } catch (err: any) {
                                    setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send email' });
                                  } finally { setReportEmailSending(false); }
                                }}
                                style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', color: colors.navy, fontWeight: 700, fontSize: 11, padding: '5px 16px' }}
                              >
                                {reportEmailSending ? <><Spinner animation="border" size="sm" className="me-1" />Sending...</> : `Send to ${selected.user_email}`}
                              </Button>
                              <Button size="sm" variant="outline-secondary" style={{ fontSize: 11 }}
                                onClick={() => { setShowReportEmail(false); setReportEmailDraft(''); setReportEmailSubject(''); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quick Email Templates — select to preview, then send */}
                    <div style={{ background: '#fff', borderRadius: 10, padding: 16, marginTop: 12, border: '1px solid #e9ecef' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: colors.navy, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                        <FontAwesomeIcon icon={faEnvelope} style={{ color: colors.gold }} />
                        Email Templates — Select to Preview
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {[
                          { id: 'report_ready', label: 'Report Ready', icon: '📄', subject: `Your AI Onboarding Report Is Ready`, body: `Hi ${selected.user_first_name},\n\nYour book of business has been classified by our AI engine. Your complete report is now available on your dashboard.\n\nHere's what we found:\n• Active prospects identified and prioritized for outreach\n• Past clients organized for re-engagement — your highest-margin opportunities\n• Sphere of influence contacts mapped for referral potential\n• Industry contacts (vendors, lenders) filtered out of your pipeline\n\nThe report includes a personalized game plan for each tier, suggested talking points for your top contacts, and commission projections based on your actual data.\n\nLog in to view your report: app.kairosiq.ai\n\nThis is the foundation of your AI-powered practice. Every month, your book gets re-scanned and you'll receive an updated intelligence briefing.\n\n— KairosIQ AI` },
                          { id: 'opportunity_ready', label: 'Opportunity Report Ready', icon: '🎯', subject: `Your Opportunity Report Is Ready`, body: `Hi ${selected.user_first_name},\n\nYour Opportunity Report is ready — a named, prioritized list of exactly who to call, why, and what to say.\n\nFor every contact: who (by name + phone/email), why our AI flagged them, the recommended next step, and AI property valuations for your seller opportunities.\n\nLog in to view your report: app.kairosiq.ai\n\nThis refreshes monthly so you always know who to call.\n\n— KairosIQ AI` },
                          { id: 'welcome', label: 'Welcome', icon: '👋', subject: `Welcome to KairosIQ AI, ${selected.user_first_name}!`, body: `Hi ${selected.user_first_name},\n\nWelcome to KairosIQ AI — your AI-powered real estate intelligence platform.\n\nTo get started, connect your email (Gmail or Outlook) so our AI can begin classifying your book of business. This takes about 60 seconds:\n\n1. Log in at app.kairosiq.ai\n2. Go to Settings → Email Integration\n3. Click "Connect Outlook" or "Connect Gmail"\n\nOnce connected, our AI reads your email threads to understand every relationship — buyers, sellers, past clients, sphere, vendors. No manual data entry needed.\n\nYou'll receive your AI Onboarding Report once classification is complete.\n\nQuestions? Reply to this email or reach us at support@kairosiq.ai.\n\n— KairosIQ AI` },
                          { id: 'connect_email', label: 'Connect Email', icon: '📧', subject: `Next Step: Connect Your Email — ${selected.user_first_name}`, body: `Hi ${selected.user_first_name},\n\nYour KairosIQ account is set up! The next step is connecting your email so our AI can classify your book of business.\n\nHere's how (takes 60 seconds):\n\n1. Log in at app.kairosiq.ai\n2. Go to Settings → Email Integration\n3. Click "Connect Outlook" or "Connect Gmail"\n4. Authorize access — we only read emails involving your clients\n\nOnce connected, classification begins automatically. You'll receive your AI Onboarding Report when it's complete.\n\nPrivacy note: We only access emails involving your clients and transaction parties. Your full inbox is never read or stored.\n\n— KairosIQ AI` },
                          { id: 'mls_connected', label: 'MLS Connected', icon: '🔗', subject: `Email Connected — Classification Running`, body: `Hi ${selected.user_first_name},\n\nGreat news — your email is now connected and AI classification has started!\n\nOur engine is reading your email history to identify every relationship in your book:\n• Active buyers and potential sellers\n• Past clients for re-engagement\n• Sphere of influence contacts\n• Industry contacts (filtered out automatically)\n\nThis process takes a few hours depending on the size of your inbox. You'll receive your AI Onboarding Report when it's complete.\n\nYou don't need to do anything — just let the AI work.\n\n— KairosIQ AI` },
                          { id: 'mls_activation', label: 'MLS $150 Activation', icon: '🔓', subject: `Unlock Property Valuations — $150/year`, body: `Hi ${selected.user_first_name},\n\nYour AI book classification is complete — and we found property addresses in your database ready for valuation.\n\nWith MLS activation ($150/year), KairosIQ runs AI-powered valuations on every property in your book:\n\n• Price predictions for every seller contact\n• Comp analysis from live MLS closed sales\n• Equity tracking — know when past clients hit milestones\n• Retention intelligence — see which clients transacted with someone else\n• Passive-seller funnel — automated alerts when properties are ready\n\nLog in to activate: app.kairosiq.ai\n\nThe activation prompt appears on your dashboard. One click to unlock.\n\n— KairosIQ AI` },
                          { id: 'broker_idx', label: 'Broker IDX', icon: '🏢', subject: `ARMLS IDX Authorization Request`, body: `This template sends the IDX authorization letter directly to the subscriber's designated broker. Click Send to deliver.` },
                        ].map(tpl => (
                          <Button key={tpl.id} size="sm"
                            variant={(reportEmailDraft && (reportEmailSubject || '').includes(tpl.subject?.substring(0, 15) || '')) ? 'primary' : 'outline-secondary'}
                            style={{ fontSize: 11, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
                            onClick={async () => {
                              if (!window.confirm(`Send "${tpl.label}" email to ${selected.user_email}?`)) return;
                              try {
                                const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/send-onboarding-email`, { template: tpl.id });
                                if (res.data.success) setAlert({ type: 'success', message: res.data.message });
                              } catch (err: any) {
                                setAlert({ type: 'danger', message: err.response?.data?.error || `Failed to send ${tpl.label}` });
                              }
                            }}
                          >
                            <span>{tpl.icon}</span> {tpl.label}
                          </Button>
                        ))}
                      </div>
                      <div style={{ fontSize: 10, color: '#999', marginTop: 6 }}>
                        Select a template to preview and edit before sending to {selected.user_first_name} ({selected.user_email})
                      </div>
                    </div>

                    {/* Start Full Onboarding button */}
                    {completedCount < steps.length && (
                      <div style={{ marginTop: 12, textAlign: 'center' }}>
                        <Button size="sm"
                          style={{ background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, border: 'none', color: colors.navy, fontWeight: 700, fontSize: 12, padding: '8px 24px' }}
                          disabled={actionLoading}
                          onClick={async () => {
                            setActionLoading(true);
                            try {
                              const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/start-onboarding`);
                              setAlert({ type: 'success', message: res.data.message || 'Onboarding started!' });
                              await fetchSubscribers();
                              selectSubscriber({ ...selected });
                            } catch (err: any) {
                              setAlert({ type: 'danger', message: err.response?.data?.error || 'Onboarding failed' });
                            } finally { setActionLoading(false); }
                          }}
                        >
                          {actionLoading ? <Spinner animation="border" size="sm" /> : '▶ Start Full Onboarding'}
                        </Button>
                        <div style={{ fontSize: 10, color: '#999', marginTop: 4 }}>Runs all incomplete steps automatically</div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* KOPS-540: Jasper AI Agent — SMS/Email + Onboarding Automation */}
              {selected && (
                <div style={{ background: 'white', borderRadius: '12px', marginBottom: '20px', border: `1px solid ${showJasper ? colors.gold : '#e9ecef'}` }}>
                  <div
                    onClick={() => setShowJasper(!showJasper)}
                    style={{ padding: '12px 16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: showJasper ? '1px solid #e9ecef' : 'none' }}
                  >
                    <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
                      <FontAwesomeIcon icon={faRobot} className="me-2" />Jasper AI Agent
                    </h6>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {jasperConversation.length > 0 && (
                        <span style={{ fontSize: 10, color: '#999' }}>{jasperConversation.length} messages</span>
                      )}
                      <span style={{ fontSize: 11, color: '#999' }}>{showJasper ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {showJasper && (
                    <div style={{ padding: '16px' }}>
                      {/* SMS/Email Conversation History */}
                      <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 16, padding: '8px', background: '#f8f9fa', borderRadius: 8 }}>
                        {jasperConversation.length === 0 ? (
                          <p style={{ color: '#999', textAlign: 'center', padding: '20px 0', fontSize: 12 }}>No Jasper messages yet for this subscriber.</p>
                        ) : (
                          jasperConversation.map((msg: any, i: number) => (
                            <div key={i} style={{
                              display: 'flex', justifyContent: msg.role === 'assistant' ? 'flex-start' : 'flex-end',
                              marginBottom: 8,
                            }}>
                              <div style={{
                                maxWidth: '80%', padding: '8px 12px', borderRadius: 12,
                                background: msg.role === 'assistant' ? colors.navy : '#e3f2fd',
                                color: msg.role === 'assistant' ? 'white' : '#333',
                                fontSize: 12, lineHeight: 1.5,
                              }}>
                                <div>{msg.content}</div>
                                <div style={{ fontSize: 9, color: msg.role === 'assistant' ? '#8FA3BF' : '#999', marginTop: 4, textAlign: 'right' }}>
                                  {msg.channel === 'sms' ? '📱' : '✉️'} {msg.role === 'assistant' ? 'Jasper' : selected.user_first_name} · {msg.created_at ? new Date(msg.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* Send via Jasper — mirrors ContactsPage pattern */}
                      <div style={{ background: '#f0f7ff', borderRadius: '8px', padding: '14px', border: '1px solid #d0e4f7' }}>
                        <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '11px', fontWeight: 700, color: colors.navy, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Send via Jasper
                          </div>
                          <div className="d-flex gap-1">
                            <button onClick={() => { setJasperChannel('sms'); setJasperPreview(''); setJasperEmailSubject(''); }}
                              style={{ background: jasperChannel === 'sms' ? colors.navy : '#eee', color: jasperChannel === 'sms' ? 'white' : '#666', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                              SMS
                            </button>
                            <button onClick={() => { setJasperChannel('email'); setJasperPreview(''); }}
                              style={{ background: jasperChannel === 'email' ? colors.navy : '#eee', color: jasperChannel === 'email' ? 'white' : '#666', border: 'none', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer' }}>
                              Email
                            </button>
                          </div>
                        </div>

                        {/* Quick onboarding tasks */}
                        <div className="d-flex gap-1 flex-wrap" style={{ marginBottom: 8 }}>
                          {[
                            { label: 'Welcome', task: 'Send a warm welcome message. Introduce yourself as Jasper from KairosIQ and let them know their account is ready.' },
                            { label: 'Connect Email', task: 'Ask them to connect their Gmail or Outlook so AI Lead Coach can discover their contacts. Include the link: app.kairosiq.ai/agent-portal/dashboard/settings' },
                            { label: 'Check In', task: 'Follow up on their onboarding progress. Ask if they need help with anything.' },
                            { label: 'IDX Status', task: 'Check in about their MLS authorization status. Ask if their broker completed the ARMLS setup.' },
                          ].map(qt => (
                            <button key={qt.label} onClick={() => setJasperTask(qt.task)}
                              style={{ background: '#e3f2fd', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 10, color: colors.navy, fontWeight: 600, cursor: 'pointer' }}>
                              {qt.label}
                            </button>
                          ))}
                        </div>

                        <textarea value={jasperTask} onChange={e => { setJasperTask(e.target.value); setJasperPreview(''); }}
                          placeholder={jasperChannel === 'sms'
                            ? 'Give Jasper a task:\n• "Welcome them and ask about their brokerage"\n• "Follow up on email connectivity"'
                            : 'Give Jasper a task:\n• "Send onboarding welcome with setup instructions"\n• "Follow up about ARMLS authorization"'}
                          rows={3} style={{ width: '100%', fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', padding: '10px', resize: 'vertical', marginBottom: '8px' }} />

                        <div className="d-flex gap-2">
                          <Button size="sm" disabled={!jasperTask.trim() || jasperLoading}
                            onClick={async () => {
                              setJasperLoading(true);
                              try {
                                const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/jasper-draft`, {
                                  task: jasperTask, channel: jasperChannel,
                                  subscriberName: selected.user_first_name + ' ' + selected.user_last_name,
                                  subscriberEmail: selected.user_email,
                                  subscriberPhone: selected.user_phone,
                                });
                                if (res.data.success) {
                                  setJasperPreview(res.data.draft);
                                  if (res.data.subject) setJasperEmailSubject(res.data.subject);
                                }
                              } catch (err: any) { setAlert({ type: 'danger', message: 'Failed to generate draft' }); }
                              setJasperLoading(false);
                            }}
                            style={{ background: colors.navy, border: 'none', fontSize: '11px', fontWeight: 600 }}>
                            {jasperLoading ? <><Spinner size="sm" className="me-1" />Drafting...</> : 'Preview Draft'}
                          </Button>
                          {jasperPreview && (
                            <Button size="sm" disabled={jasperSending}
                              onClick={async () => {
                                setJasperSending(true);
                                try {
                                  const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/jasper-send`, {
                                    message: jasperPreview, channel: jasperChannel,
                                    subject: jasperChannel === 'email' ? jasperEmailSubject : undefined,
                                    task: jasperTask,
                                  });
                                  if (res.data.success) {
                                    setAlert({ type: 'success', message: `Jasper sent ${jasperChannel} to ${selected.user_first_name}` });
                                    setJasperTask(''); setJasperPreview(''); setJasperEmailSubject('');
                                    // Refresh conversation
                                    axiosInstance.get(`/admin/subscribers/${selected.user_id}/jasper-conversation`).then(r => {
                                      if (r.data.success) setJasperConversation(r.data.messages || []);
                                    });
                                  }
                                } catch (err: any) { setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send' }); }
                                setJasperSending(false);
                              }}
                              style={{ background: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.gold2} 100%)`, border: 'none', fontSize: '11px', fontWeight: 700 }}>
                              {jasperSending ? <><Spinner size="sm" className="me-1" />Sending...</> : `Send ${jasperChannel === 'email' ? 'Email' : 'SMS'}`}
                            </Button>
                          )}
                        </div>

                        {jasperPreview && (
                          <div style={{ background: 'white', borderRadius: '6px', padding: '12px', marginTop: '10px', border: '1px solid #e0e0e0' }}>
                            <div style={{ fontSize: '10px', fontWeight: 700, color: '#999', textTransform: 'uppercase', marginBottom: '4px' }}>
                              Jasper will send ({jasperChannel}):
                            </div>
                            {jasperChannel === 'email' && (
                              <input value={jasperEmailSubject} onChange={e => setJasperEmailSubject(e.target.value)}
                                placeholder="Email subject"
                                style={{ width: '100%', fontSize: '13px', border: '1px solid #eee', borderRadius: '4px', padding: '6px 8px', marginBottom: '6px', fontWeight: 600 }} />
                            )}
                            <textarea value={jasperPreview} onChange={e => setJasperPreview(e.target.value)}
                              rows={jasperChannel === 'email' ? 6 : 3} style={{ width: '100%', fontSize: '13px', border: '1px solid #eee', borderRadius: '4px', padding: '8px', resize: 'vertical', lineHeight: 1.5 }} />
                            <div style={{ fontSize: '10px', color: '#999', marginTop: '4px' }}>Edit the message above if needed, then click Send.</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Info Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                {/* Account Info */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Account Info
                  </h6>
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}>Plan</span>
                      <span style={{ fontWeight: 600, color: getPlanColor(selected.plan_id) }}>
                        {PLAN_LABELS[selected.plan_id]} (${PLAN_PRICES[selected.plan_id]}/mo)
                      </span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}>Status</span>
                      <span style={{ fontWeight: 600, color: getStatusColor(selected.status || 'inactive'), textTransform: 'capitalize' }}>
                        {selected.status}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}>Signed Up</span>
                      <span>{formatDateShort(selected.created_at)}</span>
                    </div>
                    {selected.trial_ends_at && (
                      <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                        <span style={{ color: '#999' }}>Trial Ends</span>
                        <span style={{ color: isTrialExpiring(selected.trial_ends_at) ? colors.red : '#555', fontWeight: isTrialExpiring(selected.trial_ends_at) ? 700 : 400 }}>
                          {formatDateShort(selected.trial_ends_at)}
                        </span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}>Last Login</span>
                      <span>{formatDate(selected.last_login)}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span style={{ color: '#999' }}>Portal</span>
                      <a href={`https://${selected.subdomain}.aisearchportal.com`} target="_blank" rel="noopener noreferrer"
                        style={{ color: colors.blue, fontSize: '12px' }}>
                        {selected.subdomain}.aisearchportal.com
                      </a>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Usage Stats
                  </h6>
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}><FontAwesomeIcon icon={faPlug} className="me-1" />Connected Emails</span>
                      <span style={{ fontWeight: 600 }}>{selected.connected_emails}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}><FontAwesomeIcon icon={faAddressBook} className="me-1" />Leads</span>
                      <span style={{ fontWeight: 600 }}>{detail?.leadCount || selected.lead_count}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}><FontAwesomeIcon icon={faRobot} className="me-1" />AI Requests</span>
                      <span style={{ fontWeight: 600 }}>{detail?.aiUsage?.total_requests || 0}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #eee' }}>
                      <span style={{ color: '#999' }}><FontAwesomeIcon icon={faDollarSign} className="me-1" />AI Cost</span>
                      <span style={{ fontWeight: 600 }}>${Number(detail?.aiUsage?.total_cost || 0).toFixed(2)}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span style={{ color: '#999' }}><FontAwesomeIcon icon={faChartLine} className="me-1" />AI Coach Actions</span>
                      <span style={{ fontWeight: 600 }}>{detail?.coachActivity || 0}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Connected Emails */}
              {detail?.connectedEmails && detail.connectedEmails.length > 0 && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />Connected Emails ({detail.connectedEmails.length})
                  </h6>
                  {detail.connectedEmails.map((ce, i) => (
                    <div key={i} className="d-flex justify-content-between align-items-center" style={{ padding: '6px 0', borderBottom: i < detail.connectedEmails.length - 1 ? '1px solid #eee' : 'none' }}>
                      <div>
                        <span style={{ color: '#333', fontSize: '13px' }}>{ce.email}</span>
                        <span style={{ color: colors.muted, fontSize: '11px', marginLeft: '8px' }}>{ce.provider}</span>
                      </div>
                      <span style={{ color: colors.green, fontSize: '11px' }}>Connected {formatDateShort(ce.created_on)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Portal Settings Preview */}
              {detail?.subscriber && (detail.subscriber.brand_color || detail.subscriber.welcome_message) && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: colors.gold, fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    Portal Settings
                  </h6>
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    {detail.subscriber.brand_color && (
                      <div className="d-flex align-items-center gap-2 py-1">
                        <span style={{ color: '#999' }}>Brand Color:</span>
                        <div style={{ width: '20px', height: '20px', borderRadius: '4px', background: detail.subscriber.brand_color, border: '1px solid #ddd' }} />
                        <span>{detail.subscriber.brand_color}</span>
                      </div>
                    )}
                    {detail.subscriber.welcome_message && (
                      <div className="py-1">
                        <span style={{ color: '#999' }}>Welcome Message:</span>
                        <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#666' }}>{detail.subscriber.welcome_message}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Subscriber Lifecycle — KOPS-501 AI Employee Dashboard */}
              {lifecycle.length > 0 && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '12px' }}>
                    <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
                      <FontAwesomeIcon icon={faRocket} className="me-2" />Subscriber Lifecycle
                    </h6>
                    {lifecycleTokens > 0 && (
                      <span style={{ fontSize: '10px', color: '#999' }}>
                        {lifecycleTokens.toLocaleString()} tokens used
                      </span>
                    )}
                  </div>

                  {lifecycle.map((m: any, i: number) => {
                    const statusColor = m.status === 'complete' ? '#4caf50' :
                                       m.status === 'running' ? '#2196f3' :
                                       m.status === 'failed' ? '#f44336' :
                                       m.status === 'stale' ? '#ff9800' :
                                       m.status === 'pending' ? '#9e9e9e' : '#ccc';
                    const statusIcon = m.status === 'complete' ? '●' :
                                      m.status === 'running' ? '◎' :
                                      m.status === 'failed' ? '✕' :
                                      m.status === 'stale' ? '▲' :
                                      m.status === 'pending' ? '○' : '○';
                    const canRetry = ['failed', 'stale', 'pending'].includes(m.status) &&
                                    ['email_sync', 'classification', 'profiles_built'].includes(m.milestone);

                    return (
                      <div key={m.milestone} style={{
                        display: 'flex', alignItems: 'flex-start', gap: '10px',
                        padding: '8px 0',
                        borderBottom: i < lifecycle.length - 1 ? '1px solid #f0f0f0' : 'none'
                      }}>
                        {/* Status indicator + connector line */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '20px' }}>
                          <span style={{ color: statusColor, fontSize: '14px', fontWeight: 700 }}>{statusIcon}</span>
                          {i < lifecycle.length - 1 && (
                            <div style={{ width: '2px', height: '20px', background: m.status === 'complete' ? '#4caf5044' : '#e0e0e0', marginTop: '2px' }} />
                          )}
                        </div>

                        {/* Milestone details */}
                        <div style={{ flex: 1 }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <span style={{
                              fontSize: '13px', fontWeight: 600, textTransform: 'capitalize',
                              color: m.status === 'complete' ? '#333' : m.status === 'not_started' ? '#bbb' : '#555'
                            }}>
                              {m.milestone.replace(/_/g, ' ')}
                            </span>
                            <div className="d-flex align-items-center gap-2">
                              <span style={{
                                fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '3px',
                                background: statusColor + '18', color: statusColor
                              }}>
                                {m.status === 'not_started' ? 'waiting' : m.status}
                              </span>
                              {canRetry && (
                                <Button
                                  size="sm"
                                  variant="link"
                                  disabled={retryingMilestone === m.milestone}
                                  onClick={async () => {
                                    if (!selected) return;
                                    setRetryingMilestone(m.milestone);
                                    try {
                                      await axiosInstance.post(`/admin/subscribers/${selected.user_id}/lifecycle/retry/${m.milestone}`);
                                      setAlert({ type: 'success', message: `Retrying ${m.milestone.replace(/_/g, ' ')}...` });
                                    } catch { setAlert({ type: 'danger', message: 'Retry failed' }); }
                                    finally { setRetryingMilestone(null); }
                                  }}
                                  style={{ fontSize: '10px', padding: '0 4px', color: '#C9952A' }}
                                >
                                  {retryingMilestone === m.milestone ? '...' : '↻'}
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Timestamps */}
                          {m.completed_at && (
                            <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                              Completed {new Date(m.completed_at).toLocaleString()}
                              {m.tokens_used > 0 && ` · ${m.tokens_used} tokens`}
                            </div>
                          )}
                          {m.status === 'running' && m.triggered_at && (
                            <div style={{ fontSize: '10px', color: '#2196f3', marginTop: '2px' }}>
                              Started {new Date(m.triggered_at).toLocaleString()}
                            </div>
                          )}

                          {/* AI notes or errors */}
                          {m.error_message && (
                            <div style={{ fontSize: '10px', color: '#f44336', marginTop: '2px' }}>
                              Error: {m.error_message.substring(0, 100)}
                              {m.retry_count > 0 && ` (${m.retry_count} retries)`}
                            </div>
                          )}
                          {m.ai_notes && !m.error_message && (
                            <div style={{ fontSize: '10px', color: '#666', marginTop: '2px', fontStyle: 'italic' }}>
                              {m.ai_notes.substring(0, 120)}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pipeline Diagnostics — AI Employee View */}
              {diagnostics && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: `1px solid ${diagnostics.status === 'critical' ? '#ffcdd2' : diagnostics.status === 'needs_attention' ? '#ffe0b2' : '#e9ecef'}` }}>
                  <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '12px' }}>
                    <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
                      <FontAwesomeIcon icon={faRobot} className="me-2" />Pipeline Diagnostics
                    </h6>
                    <span style={{
                      fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '4px',
                      background: diagnostics.status === 'healthy' ? '#e8f5e9' : diagnostics.status === 'critical' ? '#fde8e8' : '#fff8e1',
                      color: diagnostics.status === 'healthy' ? '#2e7d32' : diagnostics.status === 'critical' ? '#c62828' : '#e65100'
                    }}>
                      {diagnostics.status.toUpperCase()}
                    </span>
                  </div>

                  {/* Key metrics */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#333' }}>{diagnostics.total_leads?.toLocaleString()}</div>
                      <div style={{ fontSize: '10px', color: '#999' }}>Total Leads</div>
                    </div>
                    <div style={{ background: diagnostics.classification.stuck > 0 ? '#fde8e8' : '#e8f5e9', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: diagnostics.classification.stuck > 0 ? '#c62828' : '#2e7d32' }}>
                        {diagnostics.classification.classified}
                      </div>
                      <div style={{ fontSize: '10px', color: '#999' }}>Classified</div>
                    </div>
                    <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 700, color: '#333' }}>{diagnostics.profiles.buyer + diagnostics.profiles.seller}</div>
                      <div style={{ fontSize: '10px', color: '#999' }}>Profiles</div>
                    </div>
                  </div>

                  {/* Issues */}
                  {diagnostics.issues?.length > 0 && (
                    <div style={{ marginBottom: '12px' }}>
                      {diagnostics.issues.map((issue: any, i: number) => (
                        <div key={i} style={{
                          padding: '8px 12px', marginBottom: '6px', borderRadius: '6px',
                          background: issue.severity === 'critical' ? '#fde8e8' : '#fff8e1',
                          border: `1px solid ${issue.severity === 'critical' ? '#ffcdd2' : '#ffe0b2'}`,
                          fontSize: '12px', color: issue.severity === 'critical' ? '#c62828' : '#e65100'
                        }}>
                          {issue.severity === 'critical' ? '✕ ' : '▲ '}{issue.issue}
                          {issue.detail && <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>{issue.detail}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  {diagnostics.actions?.length > 0 && (
                    <div className="d-flex gap-2 flex-wrap">
                      {diagnostics.actions.some((a: any) => a.action === 'run_full_classification') && (
                        <Button
                          size="sm"
                          variant={runningClassification ? 'secondary' : 'warning'}
                          disabled={runningClassification}
                          onClick={async () => {
                            if (!selected) return;
                            setRunningClassification(true);
                            try {
                              await axiosInstance.post(`/admin/subscribers/${selected.user_id}/run-classification`, { force: false });
                              setAlert({ type: 'success', message: `Classification started for ${diagnostics.classification.stuck} stuck leads. This runs in background.` });
                            } catch { setAlert({ type: 'danger', message: 'Failed to start classification' }); }
                          }}
                          style={{ fontSize: '11px' }}
                        >
                          {runningClassification ? <><Spinner animation="border" size="sm" /> Running...</> : `Run Classification (${diagnostics.classification.stuck} leads)`}
                        </Button>
                      )}
                      {diagnostics.actions.some((a: any) => a.action === 'run_email_backfill') && (
                        <span style={{ fontSize: '11px', color: '#e65100', padding: '4px 8px', background: '#fff3e0', borderRadius: '4px' }}>
                          {diagnostics.emails.leads_missing_emails} leads need email backfill
                        </span>
                      )}
                    </div>
                  )}

                  {/* Email stats */}
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee', fontSize: '12px', color: '#666' }}>
                    <div className="d-flex justify-content-between"><span>Leads with downloaded emails</span><span style={{ fontWeight: 600 }}>{diagnostics.emails.leads_with_emails} / {diagnostics.leads_with_email}</span></div>
                    <div className="d-flex justify-content-between"><span>Total emails in system</span><span style={{ fontWeight: 600 }}>{diagnostics.emails.total_emails?.toLocaleString()}</span></div>
                    <div className="d-flex justify-content-between"><span>Connected accounts</span><span style={{ fontWeight: 600 }}>{diagnostics.connected_accounts?.length || 0}</span></div>
                  </div>

                  {diagnostics.status === 'healthy' && (
                    <div style={{ marginTop: '10px', textAlign: 'center' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#4caf50', marginRight: '6px' }} />
                      <span style={{ fontSize: '12px', color: '#4caf50' }}>Pipeline healthy — all leads classified and profiled</span>
                    </div>
                  )}
                </div>
              )}

              {/* Classification Audit — spot-check samples */}
              {classAudit && classAudit.classification?.distribution?.length > 0 && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    <FontAwesomeIcon icon={faChartLine} className="me-2" />Classification Audit
                  </h6>

                  {/* Distribution bars */}
                  {classAudit.classification.distribution.slice(0, 8).map((d: any) => (
                    <div key={d.type} style={{ marginBottom: '8px' }}>
                      <div className="d-flex justify-content-between" style={{ fontSize: '12px', marginBottom: '2px' }}>
                        <span style={{ textTransform: 'capitalize' }}>{d.type.replace(/_/g, ' ')}</span>
                        <span style={{ fontWeight: 600 }}>{d.count.toLocaleString()} <span style={{ color: '#999', fontWeight: 400 }}>({d.pct > 100 ? '>100' : d.pct}%)</span></span>
                      </div>
                      <div style={{ height: '4px', background: '#eee', borderRadius: '2px' }}>
                        <div style={{
                          height: '100%', borderRadius: '2px',
                          width: `${Math.min(d.pct, 100)}%`,
                          background: d.type === 'new_lead' || d.type === 'discovery' || d.type === 'imported' ? '#f44336' :
                                     d.type === 'vendor' || d.type === 'system' ? '#9e9e9e' : '#C9952A'
                        }} />
                      </div>
                      {/* Sample leads */}
                      {classAudit.classification.samples?.[d.type]?.slice(0, 2).map((s: any) => (
                        <div key={s.id} style={{ fontSize: '10px', color: '#888', marginTop: '2px', paddingLeft: '8px', borderLeft: '2px solid #eee' }}>
                          {s.name} ({s.email}) — {s.email_count} emails
                          {s.summary && <span style={{ color: '#666' }}> — {s.summary.substring(0, 80)}...</span>}
                        </div>
                      ))}
                    </div>
                  ))}

                  {/* Profile completeness */}
                  <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                    <div style={{ fontSize: '11px', color: '#999', marginBottom: '6px' }}>Profile Completeness</div>
                    <div className="d-flex gap-3">
                      <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 700 }}>{classAudit.profiles.buyer.total}</div>
                        <div style={{ fontSize: '10px', color: '#999' }}>Buyer Profiles</div>
                        <div style={{ fontSize: '11px', color: classAudit.profiles.buyer.completeness_pct > 50 ? '#2e7d32' : '#e65100', fontWeight: 600 }}>
                          {classAudit.profiles.buyer.completeness_pct}% complete
                        </div>
                      </div>
                      <div style={{ flex: 1, background: '#f8f9fa', borderRadius: '6px', padding: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: 700 }}>{classAudit.profiles.seller.total}</div>
                        <div style={{ fontSize: '10px', color: '#999' }}>Seller Profiles</div>
                        <div style={{ fontSize: '11px', color: classAudit.profiles.seller.completeness_pct > 50 ? '#2e7d32' : '#e65100', fontWeight: 600 }}>
                          {classAudit.profiles.seller.completeness_pct}% complete
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Outbound Email Log */}
              {/* Email History — all outbound emails (onboarding + lifecycle + admin) */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                  <FontAwesomeIcon icon={faEnvelope} className="me-2" />Email History ({emailLog.length})
                </h6>
                {emailLog.length === 0 ? (
                  <p style={{ fontSize: '12px', color: '#999', textAlign: 'center', padding: '8px 0' }}>No emails sent yet.</p>
                ) : (
                  emailLog.slice(0, 20).map((e: any, i: number) => (
                    <div key={i}
                      onClick={() => setEmailPreview(e)}
                      style={{ padding: '8px 0', borderBottom: i < Math.min(emailLog.length, 20) - 1 ? '1px solid #f0f0f0' : 'none', cursor: 'pointer' }}
                      onMouseOver={(ev) => (ev.currentTarget.style.background = '#f8f9fa')}
                      onMouseOut={(ev) => (ev.currentTarget.style.background = 'transparent')}
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', color: '#333', fontWeight: 600 }}>
                            <span style={{ color: e.sent ? '#2e7d32' : '#c62828', marginRight: '6px' }}>{e.sent ? '●' : '✕'}</span>
                            {(e.subject || '').replace(/^[✅❌📧]\s*/, '').substring(0, 60)}
                          </div>
                          <div style={{ fontSize: '10px', color: '#999', marginTop: '2px' }}>
                            {e.type === 'onboarding' && <span style={{ background: '#C9952A22', color: '#C9952A', padding: '1px 6px', borderRadius: '3px', marginRight: '4px', fontWeight: 600 }}>Onboarding</span>}
                            {e.template && <span style={{ marginRight: '8px' }}>{e.template.replace(/_/g, ' ')}</span>}
                            {e.to && <span>→ {e.to}</span>}
                            {e.cc && <span style={{ marginLeft: '4px' }}>(cc: {e.cc})</span>}
                          </div>
                        </div>
                        <span style={{ color: '#999', fontSize: '10px', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                          {e.date ? new Date(e.date).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* AI Pipeline Health */}
              {health && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    <FontAwesomeIcon icon={faDatabase} className="me-2" />AI Pipeline Health
                  </h6>
                  <div style={{ fontSize: '13px', color: '#555' }}>
                    <div className="d-flex justify-content-between py-1">
                      <span><FontAwesomeIcon icon={health.pipeline.discoveryLeads > 0 ? faCheckCircle : faClock} className="me-1" style={{ color: health.pipeline.discoveryLeads > 0 ? '#4caf50' : '#999' }} />Discovered Leads</span>
                      <span style={{ fontWeight: 600 }}>{health.pipeline.discoveryLeads}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span><FontAwesomeIcon icon={health.pipeline.emailsStored > 0 ? faCheckCircle : faClock} className="me-1" style={{ color: health.pipeline.emailsStored > 0 ? '#4caf50' : '#999' }} />Emails Stored</span>
                      <span style={{ fontWeight: 600 }}>{health.pipeline.emailsStored.toLocaleString()}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span><FontAwesomeIcon icon={health.pipeline.classified > 0 ? faCheckCircle : faClock} className="me-1" style={{ color: health.pipeline.classified > 0 ? '#4caf50' : '#999' }} />Classified</span>
                      <span style={{ fontWeight: 600 }}>{health.pipeline.classified}</span>
                    </div>
                    <div className="d-flex justify-content-between py-1">
                      <span><FontAwesomeIcon icon={faUsers} className="me-1" style={{ color: '#2196f3' }} />Profiles</span>
                      <span style={{ fontWeight: 600 }}>{health.pipeline.buyerProfiles} buyer / {health.pipeline.sellerProfiles} seller</span>
                    </div>
                  </div>

                  {/* Classification Breakdown */}
                  {health.classifications?.length > 0 && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                      <div style={{ fontSize: '11px', color: '#999', marginBottom: '6px' }}>Classification Distribution</div>
                      {health.classifications.filter((c: any) => c.type !== 'imported').map((c: any) => (
                        <div key={c.type} className="d-flex justify-content-between" style={{ fontSize: '12px', padding: '2px 0' }}>
                          <span style={{ textTransform: 'capitalize' }}>{c.type.replace(/_/g, ' ')}</span>
                          <span style={{ fontWeight: 600 }}>{c.count}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Compliance */}
                  {health.compliance?.totalViolations > 0 && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                      <div style={{ fontSize: '11px', color: '#f44336', fontWeight: 700, marginBottom: '6px' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                        {health.compliance.totalViolations} Compliance Flags (30d)
                      </div>
                      {health.compliance.violations.map((v: any) => (
                        <div key={v.type} className="d-flex justify-content-between" style={{ fontSize: '12px', padding: '2px 0', color: '#d32f2f' }}>
                          <span>{v.type.replace(/_/g, ' ')}</span>
                          <span style={{ fontWeight: 600 }}>{v.count}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Errors */}
                  {health.errors?.length > 0 && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                      <div style={{ fontSize: '11px', color: '#ff9800', fontWeight: 700, marginBottom: '6px' }}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className="me-1" />
                        {health.errors.length} Pipeline Errors (7d)
                      </div>
                      {health.errors.map((e: any, i: number) => (
                        <div key={i} style={{ fontSize: '11px', color: '#e65100', padding: '2px 0' }}>
                          {e.subject} — {new Date(e.date).toLocaleDateString()}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* AI Costs */}
                  {health.costs?.total > 0 && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee' }}>
                      <div className="d-flex justify-content-between" style={{ fontSize: '12px' }}>
                        <span style={{ color: '#999' }}>AI Cost (30d)</span>
                        <span style={{ fontWeight: 700, color: '#2e7d32' }}>${health.costs.total.toFixed(2)}</span>
                      </div>
                      {health.costs.breakdown.map((c: any) => (
                        <div key={c.type} className="d-flex justify-content-between" style={{ fontSize: '11px', padding: '1px 0', color: '#888' }}>
                          <span>{c.type.replace(/_/g, ' ')}</span>
                          <span>${c.cost.toFixed(4)} ({c.calls} calls)</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* No issues */}
                  {(!health.errors || health.errors.length === 0) && (!health.compliance || health.compliance.totalViolations === 0) && (
                    <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #eee', textAlign: 'center' }}>
                      <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#4caf50', marginRight: '6px' }} />
                      <span style={{ fontSize: '12px', color: '#4caf50' }}>All systems healthy — no errors or violations</span>
                    </div>
                  )}
                </div>
              )}

              {/* Pipeline Runs — KOPS-501 Orchestrator History */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: '12px' }}>
                  <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
                    <FontAwesomeIcon icon={faRocket} className="me-2" />Pipeline Runs
                  </h6>
                  <Button
                    size="sm"
                    variant="outline-warning"
                    disabled={pipelineRetrying}
                    onClick={async () => {
                      if (!selected) return;
                      setPipelineRetrying(true);
                      try {
                        await axiosInstance.post(`/admin/subscribers/${selected.user_id}/pipeline-retry`);
                        setAlert({ type: 'success', message: 'Pipeline triggered! Check runs in ~2 minutes.' });
                      } catch (err: any) {
                        setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to trigger pipeline' });
                      } finally {
                        setPipelineRetrying(false);
                      }
                    }}
                    style={{ fontSize: '11px', padding: '2px 10px' }}
                  >
                    {pipelineRetrying ? <Spinner animation="border" size="sm" /> : '↻ Run Pipeline'}
                  </Button>
                </div>

                {pipelineRuns.length === 0 ? (
                  <div style={{ fontSize: '12px', color: '#999', textAlign: 'center', padding: '12px 0' }}>
                    No pipeline runs recorded yet. Click "Run Pipeline" to trigger one.
                  </div>
                ) : (
                  <div>
                    {pipelineRuns.slice(0, 5).map((run) => (
                      <div key={run.run_id} style={{
                        padding: '10px', marginBottom: '8px', borderRadius: '8px',
                        background: run.status === 'healthy' ? '#f0f9f0' :
                                   run.status === 'needs_attention' ? '#fff8e1' :
                                   run.status === 'failed' || run.status === 'critical' ? '#fde8e8' : '#f5f5f5',
                        border: `1px solid ${
                          run.status === 'healthy' ? '#c8e6c9' :
                          run.status === 'needs_attention' ? '#ffe0b2' :
                          run.status === 'failed' || run.status === 'critical' ? '#ffcdd2' : '#e0e0e0'
                        }`
                      }}>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span style={{
                              fontSize: '11px', fontWeight: 700, padding: '1px 6px', borderRadius: '3px',
                              background: run.status === 'healthy' ? '#4caf5022' :
                                         run.status === 'needs_attention' ? '#ff980022' :
                                         run.status === 'failed' || run.status === 'critical' ? '#f4433622' : '#9e9e9e22',
                              color: run.status === 'healthy' ? '#4caf50' :
                                     run.status === 'needs_attention' ? '#ff9800' :
                                     run.status === 'failed' || run.status === 'critical' ? '#f44336' : '#999'
                            }}>
                              {run.status === 'healthy' ? '● Healthy' :
                               run.status === 'needs_attention' ? '▲ Needs Attention' :
                               run.status === 'critical' ? '✕ Critical' :
                               run.status === 'failed' ? '✕ Failed' : run.status}
                            </span>
                            {run.health_score != null && run.health_score >= 0 && (
                              <span style={{ fontSize: '11px', color: '#666', marginLeft: '8px' }}>
                                Score: {run.health_score}/100
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: '10px', color: '#999' }}>
                            {run.trigger && <span style={{ marginRight: '8px', textTransform: 'capitalize' }}>{run.trigger}</span>}
                            {run.started_at && new Date(run.started_at).toLocaleString()}
                            {run.elapsed_seconds && ` (${run.elapsed_seconds}s)`}
                          </div>
                        </div>

                        {/* Steps */}
                        {run.steps.length > 0 && (
                          <div style={{ marginTop: '6px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {run.steps.map((step, i) => (
                              <span key={i} style={{
                                fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                                background: step.status === 'complete' ? '#e8f5e9' :
                                           step.status === 'running' ? '#e3f2fd' :
                                           step.status === 'skipped' ? '#f5f5f5' : '#fce4ec',
                                color: step.status === 'complete' ? '#2e7d32' :
                                       step.status === 'running' ? '#1565c0' :
                                       step.status === 'skipped' ? '#999' : '#c62828'
                              }}>
                                {step.step.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Issues */}
                        {run.issues && run.issues.length > 0 && (
                          <div style={{ marginTop: '6px' }}>
                            {run.issues.map((issue, i) => (
                              <div key={i} style={{
                                fontSize: '11px', padding: '2px 0',
                                color: issue.severity === 'critical' ? '#c62828' :
                                       issue.severity === 'warning' ? '#e65100' : '#666'
                              }}>
                                {issue.severity === 'critical' ? '✕' : issue.severity === 'warning' ? '▲' : 'ℹ'} [{issue.step}] {issue.message}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Recommendation */}
                        {run.recommendation && (
                          <div style={{ marginTop: '4px', fontSize: '11px', color: '#1565c0', fontStyle: 'italic' }}>
                            💡 {run.recommendation}
                          </div>
                        )}

                        {/* Error (for failed runs) */}
                        {run.error && (
                          <div style={{ marginTop: '4px', fontSize: '11px', color: '#c62828' }}>
                            Failed at {run.failed_step?.replace(/_/g, ' ')}: {run.error}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Onboarding Email Templates — KOPS-539 */}
              {selected && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    <FontAwesomeIcon icon={faRocket} className="me-2" />Onboarding Emails
                  </h6>
                  <div className="d-flex gap-2 flex-wrap mb-2">
                    {[
                      { label: 'Welcome Email', key: 'welcome', icon: '👋' },
                      { label: 'ARMLS IDX → Broker', key: 'broker_idx', icon: '🔑' },
                      { label: 'Connect Email', key: 'connect_email', icon: '📧' },
                      { label: 'MLS Connected', key: 'mls_connected', icon: '✅' },
                    ].map(tpl => (
                      <Button key={tpl.key} size="sm" variant="outline-secondary"
                        style={{ fontSize: '11px', padding: '4px 10px' }}
                        disabled={actionLoading}
                        onClick={async () => {
                          setActionLoading(true);
                          try {
                            const res = await axiosInstance.post(`/admin/subscribers/${selected.user_id}/send-onboarding-email`, { template: tpl.key });
                            setAlert({ type: 'success', message: res.data.message || `${tpl.label} sent!` });
                            // Refresh email log
                            axiosInstance.get(`/admin/subscribers/${selected.user_id}/emails`).then(r => {
                              if (r.data.success) setEmailLog(r.data.emails || []);
                            }).catch(() => {});
                          } catch (err: any) {
                            setAlert({ type: 'danger', message: err.response?.data?.error || `Failed to send ${tpl.label}` });
                          } finally { setActionLoading(false); }
                        }}>
                        {tpl.icon} {tpl.label}
                      </Button>
                    ))}
                  </div>
                  <p style={{ fontSize: '10px', color: '#999', margin: '0 0 8px' }}>
                    From: onboarding@kairosiq.ai — Broker IDX email includes ARMLS PDF attachment
                  </p>
                </div>
              )}

              {/* Email Compose — send to subscriber from support@kairosiq.ai */}
              {selected && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    <FontAwesomeIcon icon={faEnvelope} className="me-2" />Send Custom Email
                  </h6>
                  <Form.Control
                    size="sm"
                    placeholder="Subject"
                    value={composeSubject}
                    onChange={(e) => setComposeSubject(e.target.value)}
                    style={{ marginBottom: '8px', fontSize: '13px' }}
                  />
                  <Form.Control
                    as="textarea"
                    rows={3}
                    size="sm"
                    placeholder="Message body (plain text — will be styled in navy+gold template)"
                    value={composeMessage}
                    onChange={(e) => setComposeMessage(e.target.value)}
                    style={{ marginBottom: '8px', fontSize: '13px' }}
                  />
                  <div className="d-flex justify-content-between align-items-center">
                    <span style={{ fontSize: '10px', color: '#999' }}>From: support@kairosiq.ai → {selected.user_email}</span>
                    <Button
                      size="sm"
                      variant="warning"
                      disabled={sendingEmail || !composeSubject || !composeMessage}
                      onClick={async () => {
                        setSendingEmail(true);
                        try {
                          await axiosInstance.post(`/admin/subscribers/${selected.user_id}/send-email`, {
                            subject: composeSubject,
                            message: composeMessage
                          });
                          setAlert({ type: 'success', message: `Email sent to ${selected.user_email}` });
                          setComposeSubject('');
                          setComposeMessage('');
                          // Refresh email log
                          axiosInstance.get(`/admin/subscribers/${selected.user_id}/emails`).then(r => {
                            if (r.data.success) setEmailLog(r.data.emails || []);
                          }).catch(() => {});
                        } catch (err: any) {
                          setAlert({ type: 'danger', message: err.response?.data?.error || 'Failed to send email' });
                        } finally {
                          setSendingEmail(false);
                        }
                      }}
                      style={{ fontSize: '11px' }}
                    >
                      {sendingEmail ? <Spinner animation="border" size="sm" /> : 'Send Email'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Activity Log — full timeline of system events + admin notes */}
              <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                  <FontAwesomeIcon icon={faClock} className="me-2" />Activity Log ({activityLog.length})
                </h6>
                {/* Add Note */}
                <div className="d-flex gap-2 mb-3">
                  <input
                    placeholder="Add an admin note for this subscriber..."
                    id="admin-note-input"
                    style={{ flex: 1, background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '6px', padding: '8px 12px', color: '#333', fontSize: '12px', outline: 'none' }}
                    onKeyDown={async (e: any) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const note = e.target.value.trim();
                        e.target.value = '';
                        try {
                          await axiosInstance.post(`/admin/subscribers/${selected.user_id}/note`, { note });
                          setAlert({ type: 'success', message: 'Note added' });
                          // Refresh activity log
                          const r = await axiosInstance.get(`/admin/subscribers/${selected.user_id}/activity-log`);
                          if (r.data?.success) setActivityLog(r.data.activities || []);
                        } catch (err: any) {
                          setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to add note' });
                        }
                      }
                    }}
                  />
                  <Button size="sm"
                    style={{ background: '#C9952A', border: 'none', fontWeight: 600, fontSize: '12px' }}
                    onClick={async () => {
                      const input = document.getElementById('admin-note-input') as HTMLInputElement;
                      if (!input?.value.trim()) return;
                      const note = input.value.trim();
                      input.value = '';
                      try {
                        await axiosInstance.post(`/admin/subscribers/${selected.user_id}/note`, { note });
                        setAlert({ type: 'success', message: 'Note added' });
                        const r = await axiosInstance.get(`/admin/subscribers/${selected.user_id}/activity-log`);
                        if (r.data?.success) setActivityLog(r.data.activities || []);
                      } catch (err: any) {
                        setAlert({ type: 'danger', message: err?.response?.data?.error || 'Failed to add note' });
                      }
                    }}>
                    Add
                  </Button>
                </div>
                {activityLog.length > 0 && (
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {activityLog.map((a: any, i: number) => {
                      const typeColors: Record<string, string> = {
                        pipeline_run: '#2196f3',
                        pipeline_error: '#f44336',
                        ops_alert: '#ff9800',
                        lifecycle_email: '#4caf50',
                        lifecycle_agent: '#9c27b0',
                        account_status: '#e74c3c',
                        admin_note: '#607d8b'
                      };
                      const color = typeColors[a.type] || '#999';
                      return (
                        <div key={i} style={{
                          padding: '6px 0',
                          borderBottom: i < activityLog.length - 1 ? '1px solid #f0f0f0' : 'none',
                          fontSize: '12px'
                        }}>
                          <div className="d-flex justify-content-between">
                            <div>
                              <span style={{
                                fontSize: '9px', fontWeight: 700, padding: '1px 4px', borderRadius: '3px',
                                background: color + '18', color, marginRight: '6px', textTransform: 'uppercase'
                              }}>
                                {a.type.replace(/_/g, ' ')}
                              </span>
                              <span style={{ color: '#333' }}>{(a.subject || '').substring(0, 60)}</span>
                            </div>
                            <span style={{ color: '#999', fontSize: '10px', whiteSpace: 'nowrap', marginLeft: '8px' }}>
                              {a.date ? new Date(a.date).toLocaleString() : ''}
                            </span>
                          </div>
                          {a.description && typeof a.description === 'string' && !a.description.startsWith('{') && (
                            <div style={{ fontSize: '11px', color: '#555', marginTop: '3px', paddingLeft: '4px', lineHeight: 1.5 }}>
                              {a.description}
                            </div>
                          )}
                          {a.subtype && !a.description && (
                            <div style={{ fontSize: '10px', color: '#888', marginTop: '2px', paddingLeft: '4px' }}>
                              {a.subtype.replace(/_/g, ' ')}
                              {a.meta?.health_score != null && ` · score: ${a.meta.health_score}`}
                              {a.meta?.error && ` · ${a.meta.error.substring(0, 60)}`}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Stripe Payments */}
              {payments && (
                <div style={{ background: 'white', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #e9ecef' }}>
                  <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '12px' }}>
                    <FontAwesomeIcon icon={faDollarSign} className="me-2" />Payment History
                  </h6>

                  {/* Subscription Status */}
                  {payments.subscription ? (
                    <div style={{ background: payments.subscription.status === 'active' ? '#f0f9f0' : '#fff3e0', borderRadius: '8px', padding: '10px', marginBottom: '10px', border: `1px solid ${payments.subscription.status === 'active' ? '#c8e6c9' : '#ffe0b2'}` }}>
                      <div className="d-flex justify-content-between" style={{ fontSize: '13px' }}>
                        <span style={{ fontWeight: 600 }}>Subscription</span>
                        <span style={{ fontWeight: 700, color: payments.subscription.status === 'active' ? '#2e7d32' : '#e65100', textTransform: 'uppercase', fontSize: '11px' }}>
                          {payments.subscription.status}
                          {payments.subscription.cancel_at_period_end && ' (cancels at period end)'}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        ${((payments.subscription.plan_amount || 0) / 100).toFixed(2)}/{payments.subscription.plan_interval || 'month'}
                        {payments.subscription.current_period_end && ` — renews ${new Date(payments.subscription.current_period_end * 1000).toLocaleDateString()}`}
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>No active subscription</div>
                  )}

                  {/* Invoices */}
                  {payments.invoices?.length > 0 ? (
                    <div>
                      {payments.invoices.slice(0, 10).map((inv: any) => (
                        <div key={inv.id} className="d-flex justify-content-between align-items-center" style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0', fontSize: '12px' }}>
                          <div>
                            <span style={{ color: '#333' }}>{inv.number || inv.id.substring(0, 15)}</span>
                            <span style={{ color: '#999', marginLeft: '8px' }}>{new Date(inv.created * 1000).toLocaleDateString()}</span>
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ fontWeight: 600 }}>${((inv.amount_paid || inv.total || 0) / 100).toFixed(2)}</span>
                            <span style={{
                              fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '4px',
                              background: inv.status === 'paid' ? '#e8f5e9' : inv.status === 'open' ? '#fff3e0' : '#f5f5f5',
                              color: inv.status === 'paid' ? '#2e7d32' : inv.status === 'open' ? '#e65100' : '#999'
                            }}>
                              {inv.status}
                            </span>
                            {inv.hosted_invoice_url && (
                              <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" style={{ fontSize: '10px', color: '#C9952A' }}>View</a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#999', textAlign: 'center', padding: '10px 0' }}>No invoices yet</div>
                  )}
                </div>
              )}
              </>}
            </div>
          )}
        </div>
      </div>

      {/* Add User Modal */}
      <Modal show={showAddUser} onHide={() => setShowAddUser(false)} centered>
        <Modal.Header closeButton style={{ background: colors.navy, color: colors.white, borderBottom: `1px solid ${colors.navy3}` }}>
          <Modal.Title style={{ fontSize: '16px', fontWeight: 700 }}>Add User</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: colors.navy2 }}>
          <div className="mb-3">
            <label style={{ color: colors.muted, fontSize: '12px', marginBottom: '4px', display: 'block' }}>Account Type *</label>
            <div className="d-flex gap-2">
              {['agent', 'broker', 'internal'].map(t => (
                <button key={t} onClick={() => setNewUser({ ...newUser, accountType: t })}
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: `1px solid ${newUser.accountType === t ? colors.gold : colors.navy3}`, background: newUser.accountType === t ? colors.gold + '22' : colors.navy3, color: newUser.accountType === t ? colors.gold : colors.muted, fontWeight: 600, fontSize: '13px', cursor: 'pointer', textTransform: 'capitalize' }}>
                  {t === 'internal' ? 'Internal Employee' : t === 'broker' ? 'Broker' : 'Agent'}
                </button>
              ))}
            </div>
          </div>
          <div className="d-flex gap-2 mb-3">
            <div style={{ flex: 1 }}>
              <label style={{ color: colors.muted, fontSize: '12px', marginBottom: '4px', display: 'block' }}>First Name *</label>
              <input value={newUser.firstName} onChange={e => setNewUser({ ...newUser, firstName: e.target.value })}
                style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 12px', color: colors.white, fontSize: '13px' }} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ color: colors.muted, fontSize: '12px', marginBottom: '4px', display: 'block' }}>Last Name *</label>
              <input value={newUser.lastName} onChange={e => setNewUser({ ...newUser, lastName: e.target.value })}
                style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 12px', color: colors.white, fontSize: '13px' }} />
            </div>
          </div>
          <div className="mb-3">
            <label style={{ color: colors.muted, fontSize: '12px', marginBottom: '4px', display: 'block' }}>Email *</label>
            <input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })}
              style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 12px', color: colors.white, fontSize: '13px' }} />
          </div>
          <div className="mb-3">
            <label style={{ color: colors.muted, fontSize: '12px', marginBottom: '4px', display: 'block' }}>Password *</label>
            <input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })}
              style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 12px', color: colors.white, fontSize: '13px' }} />
          </div>
          <div className="mb-3">
            <label style={{ color: colors.muted, fontSize: '12px', marginBottom: '4px', display: 'block' }}>Phone</label>
            <input value={newUser.phone} onChange={e => setNewUser({ ...newUser, phone: e.target.value })}
              style={{ width: '100%', background: colors.navy3, border: 'none', borderRadius: '6px', padding: '8px 12px', color: colors.white, fontSize: '13px' }} />
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: colors.navy, borderTop: `1px solid ${colors.navy3}` }}>
          <Button variant="secondary" size="sm" onClick={() => setShowAddUser(false)}>Cancel</Button>
          <Button size="sm" onClick={handleAddUser} disabled={addingUser || !newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password}
            style={{ background: colors.gold, border: 'none', color: colors.navy, fontWeight: 700 }}>
            {addingUser ? 'Creating...' : `Create ${newUser.accountType === 'internal' ? 'Internal Employee' : newUser.accountType === 'broker' ? 'Broker' : 'Agent'}`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Email Preview Modal — KOPS-539 */}
      <Modal show={!!emailPreview} onHide={() => setEmailPreview(null)} centered size="lg">
        <Modal.Header closeButton style={{ background: colors.navy, color: colors.white, borderBottom: `1px solid ${colors.navy3}` }}>
          <Modal.Title style={{ fontSize: '14px', fontWeight: 700 }}>
            <FontAwesomeIcon icon={faEnvelope} className="me-2" />
            Email Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#f8f9fa', padding: 0 }}>
          {emailPreview && (
            <div>
              {/* Email metadata */}
              <div style={{ padding: '16px 20px', background: '#fff', borderBottom: '1px solid #e9ecef' }}>
                <table style={{ width: '100%', fontSize: '13px' }}>
                  <tbody>
                    <tr>
                      <td style={{ color: '#999', width: 80, paddingBottom: 6 }}>Subject:</td>
                      <td style={{ color: '#333', fontWeight: 600 }}>{(emailPreview.subject || '').replace(/^[✅❌📧]\s*/, '')}</td>
                    </tr>
                    <tr>
                      <td style={{ color: '#999', paddingBottom: 6 }}>To:</td>
                      <td style={{ color: '#333' }}>{emailPreview.to || selected?.user_email || '—'}</td>
                    </tr>
                    {emailPreview.cc && (
                      <tr>
                        <td style={{ color: '#999', paddingBottom: 6 }}>CC:</td>
                        <td style={{ color: '#333' }}>{emailPreview.cc}</td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ color: '#999', paddingBottom: 6 }}>From:</td>
                      <td style={{ color: '#333' }}>onboarding@kairosiq.ai</td>
                    </tr>
                    <tr>
                      <td style={{ color: '#999', paddingBottom: 6 }}>Date:</td>
                      <td style={{ color: '#333' }}>{emailPreview.date ? new Date(emailPreview.date).toLocaleString() : '—'}</td>
                    </tr>
                    <tr>
                      <td style={{ color: '#999', paddingBottom: 6 }}>Type:</td>
                      <td>
                        <span style={{
                          background: emailPreview.type === 'onboarding' ? '#C9952A22' : '#e3f2fd',
                          color: emailPreview.type === 'onboarding' ? '#C9952A' : '#1976d2',
                          padding: '2px 8px', borderRadius: '4px', fontWeight: 600, fontSize: '11px'
                        }}>
                          {emailPreview.type === 'onboarding' ? 'Onboarding' : 'Lifecycle'}
                        </span>
                        {emailPreview.template && (
                          <span style={{ marginLeft: 8, color: '#666', fontSize: '12px' }}>
                            {emailPreview.template.replace(/_/g, ' ')}
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td style={{ color: '#999' }}>Status:</td>
                      <td>
                        <span style={{ color: emailPreview.sent ? '#2e7d32' : '#c62828', fontWeight: 600, fontSize: '12px' }}>
                          {emailPreview.sent ? '● Delivered' : '✕ Failed'}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Rendered email preview */}
              {emailPreview.details?.html_body && (
                <div style={{ padding: '16px 20px', background: '#fff', borderTop: '1px solid #e9ecef' }}>
                  <p style={{ fontSize: '11px', color: '#999', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Email Preview</p>
                  <iframe
                    srcDoc={emailPreview.details.html_body}
                    style={{ width: '100%', height: 500, border: '1px solid #e9ecef', borderRadius: 6 }}
                    sandbox="allow-same-origin"
                    title="Email preview"
                  />
                </div>
              )}

              {/* Email details/metadata */}
              {emailPreview.details && Object.keys(emailPreview.details).length > 0 && (
                <div style={{ padding: '16px 20px', background: '#fff', borderTop: '1px solid #e9ecef' }}>
                  <p style={{ fontSize: '11px', color: '#999', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Send Details</p>
                  <div style={{ background: '#f8f9fa', borderRadius: 6, padding: 12, fontSize: '12px', fontFamily: 'monospace', color: '#555', maxHeight: 200, overflow: 'auto' }}>
                    {Object.entries(emailPreview.details)
                      .filter(([k]) => !['sent', 'message_id', 'html_body'].includes(k))
                      .map(([key, val]) => (
                        <div key={key} style={{ marginBottom: 4 }}>
                          <span style={{ color: '#C9952A', fontWeight: 600 }}>{key}:</span>{' '}
                          <span>{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                        </div>
                      ))
                    }
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer style={{ background: '#fff', borderTop: '1px solid #e9ecef' }}>
          <Button variant="secondary" size="sm" onClick={() => setEmailPreview(null)}>Close</Button>
        </Modal.Footer>
      </Modal>

      {/* Support Chat Modal */}
      <Modal show={showSupportChat} onHide={() => setShowSupportChat(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: colors.navy, color: colors.white, borderBottom: `1px solid ${colors.navy3}` }}>
          <Modal.Title style={{ fontSize: '14px', fontWeight: 700 }}>
            <FontAwesomeIcon icon={faComments} className="me-2" />
            Support Chat — {selected?.user_first_name} {selected?.user_last_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#f8f9fa', padding: 0, height: '400px', display: 'flex', flexDirection: 'column' }}>
          {supportLoading ? (
            <div className="d-flex align-items-center justify-content-center" style={{ flex: 1 }}>
              <Spinner size="sm" className="me-2" /> Loading conversation...
            </div>
          ) : (
            <>
              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                {supportMessages.length === 0 && (
                  <div style={{ textAlign: 'center', color: '#999', padding: '40px 0', fontSize: '13px' }}>
                    No messages yet. Start the conversation below.
                  </div>
                )}
                {supportMessages.map((msg: any) => (
                  <div key={msg.id} style={{ marginBottom: '12px', display: 'flex', justifyContent: msg.role === 'assistant' ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '70%',
                      padding: '10px 14px',
                      borderRadius: msg.role === 'assistant' ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                      background: msg.role === 'assistant' ? colors.navy : '#fff',
                      color: msg.role === 'assistant' ? '#fff' : '#333',
                      border: msg.role === 'assistant' ? 'none' : '1px solid #e0e0e0',
                      fontSize: '13px', lineHeight: 1.5
                    }}>
                      <div style={{ fontSize: '10px', fontWeight: 600, marginBottom: '4px', color: msg.role === 'assistant' ? colors.gold : '#999' }}>
                        {msg.role === 'assistant' ? 'KairosIQ Support' : `${selected?.user_first_name || 'Subscriber'}`}
                      </div>
                      {msg.content}
                      <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.6 }}>
                        {new Date(msg.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Input */}
              <div style={{ borderTop: '1px solid #e9ecef', padding: '12px 16px', background: '#fff', display: 'flex', gap: '8px' }}>
                <Form.Control
                  type="text"
                  placeholder="Type a message..."
                  value={supportMsg}
                  onChange={(e) => setSupportMsg(e.target.value)}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter' && !e.shiftKey && supportMsg.trim()) {
                      e.preventDefault();
                      setSendingSupport(true);
                      try {
                        const res = await axiosInstance.post('/chat/support/send', {
                          conversationId: supportConvId,
                          message: supportMsg.trim(),
                          subscriberUserId: selected?.user_id
                        });
                        if (res.data.success) {
                          setSupportMessages(prev => [...prev, res.data.message]);
                          setSupportMsg('');
                        }
                      } catch { /* ignore */ }
                      setSendingSupport(false);
                    }
                  }}
                  style={{ fontSize: '13px' }}
                  disabled={sendingSupport}
                />
                <Button size="sm"
                  style={{ background: colors.navy, border: 'none', whiteSpace: 'nowrap' }}
                  disabled={sendingSupport || !supportMsg.trim()}
                  onClick={async () => {
                    setSendingSupport(true);
                    try {
                      const res = await axiosInstance.post('/chat/support/send', {
                        conversationId: supportConvId,
                        message: supportMsg.trim(),
                        subscriberUserId: selected?.user_id
                      });
                      if (res.data.success) {
                        setSupportMessages(prev => [...prev, res.data.message]);
                        setSupportMsg('');
                      }
                    } catch { /* ignore */ }
                    setSendingSupport(false);
                  }}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                </Button>
              </div>
            </>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

// ===========================================================================
// KOPS-465 — Brokerage Relationship Card
//
// Renders the stacked subscription layers for an individual subscriber:
//   Layer 1: Brokerage Relationship (read-only, from brokerage_team_members)
//   Layer 2: Individual Subscription (editable, from agent_portals — shown
//            in the existing Info Cards below this component)
//
// If the subscriber has no active brokerage_team_members row, renders a
// "Solo subscriber" state so admin knows they're not under any umbrella.
// ===========================================================================

// ===========================================================================
// MLSCredentialsCard — admin card for storing per-user ARMLS API credentials
// in AWS Secrets Manager. Shown for every subscriber in the detail view;
// the backend write path is the same for solo subscribers and broker admins,
// and the mls-credential-router.service.js fallback automatically routes
// agents under a broker to their broker admin's stored credentials.
//
// Fetches current status on mount (GET .../mls-credentials/status) to show
// Configured/Not Configured badge + masked key preview. Save writes to
// POST .../mls-credentials. Delete soft-deletes via DELETE (30-day recovery).
// ===========================================================================

interface MLSStatus {
  configured: boolean;
  masked_oauth_key: string | null;
  last_updated: string | null;
  target_user_email?: string;
  target_user_role?: string;
  // KOPS-620 — vendor feed fields
  feed_type?: 'vendor' | 'broker' | 'none' | 'pending' | 'suspended';
  mls_feed_type?: string;
  mls_activation_paid?: boolean;
  mls_activation_date?: string;
  vendor_feed_active?: boolean;
  last_compliance_check?: { check_result: string; checked_at: string } | null;
  note?: string;
  // KOPS-465 — populated when the target user has no direct creds
  // but belongs to a brokerage whose admin does. Lets us render
  // "Inherited from Acme Realty" instead of a scary "Not Configured"
  // banner for agents who actually ARE covered by their brokerage.
  inherited_from?: {
    brokerage_id: string;
    brokerage_name: string | null;
    admin_user_id: string;
    admin_name: string | null;
    admin_email: string | null;
    masked_oauth_key: string | null;
  } | null;
}

// Declared as `function` (not const arrow) so it's hoisted — the main
// SubscribersPage component above renders <MLSCredentialsCard /> and
// TypeScript treats forward const-arrow-function refs as errors.
function MLSCredentialsCard({ subscriberId }: { subscriberId: string }) {
  const [status, setStatus] = useState<MLSStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    oauth_key: '',
    access_token: '',
    refresh_token: '',
    api_url: 'https://replication.sparkapi.com/v1/',
  });
  const [alert, setAlert] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  // Load current status on mount and whenever the selected subscriber changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setAlert(null);
    setEditing(false);
    setForm({ oauth_key: '', access_token: '', refresh_token: '', api_url: 'https://replication.sparkapi.com/v1/' });
    (async () => {
      try {
        const res = await axiosInstance.get(`/admin/subscribers/${subscriberId}/mls-credentials/status`);
        if (!cancelled) setStatus(res.data?.data || null);
      } catch (err: any) {
        if (!cancelled) {
          // 403 means the current admin user isn't an InternalEmployee — just hide the card
          if (err?.response?.status === 403) {
            setStatus(null);
          } else {
            console.warn('Failed to load MLS status:', err);
            setStatus({ configured: false, masked_oauth_key: null, last_updated: null });
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [subscriberId]);

  const handleSave = async () => {
    if (!form.oauth_key.trim() || !form.access_token.trim()) {
      setAlert({ type: 'danger', message: 'OAuth Key and Access Token are required' });
      return;
    }
    setSaving(true);
    setAlert(null);
    try {
      await axiosInstance.post(`/admin/subscribers/${subscriberId}/mls-credentials`, form);
      setAlert({ type: 'success', message: '✅ ARMLS credentials stored in AWS Secrets Manager' });
      setEditing(false);
      // Refresh status to show the new configured state
      const res = await axiosInstance.get(`/admin/subscribers/${subscriberId}/mls-credentials/status`);
      setStatus(res.data?.data || null);
    } catch (err: any) {
      setAlert({
        type: 'danger',
        message: err?.response?.data?.error || err?.message || 'Failed to store credentials',
      });
    } finally {
      setSaving(false);
      setTimeout(() => setAlert(null), 5000);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Remove ARMLS credentials for this subscriber? Their MLS access will stop immediately. This is soft-deleted and can be recovered within 30 days from AWS.')) return;
    setSaving(true);
    try {
      await axiosInstance.delete(`/admin/subscribers/${subscriberId}/mls-credentials`);
      setAlert({ type: 'success', message: '🗑️ Credentials removed (30-day recovery window)' });
      setStatus({ configured: false, masked_oauth_key: null, last_updated: null });
    } catch (err: any) {
      setAlert({
        type: 'danger',
        message: err?.response?.data?.error || 'Failed to remove credentials',
      });
    } finally {
      setSaving(false);
      setTimeout(() => setAlert(null), 5000);
    }
  };

  // Hide card entirely if the current admin user isn't privileged or we're still loading
  if (loading) {
    return (
      <div style={{ background: '#162844', borderRadius: '12px', padding: '16px', marginBottom: '20px', border: '1px solid #1E3557' }}>
        <div style={{ fontSize: '12px', color: '#8FA3BF' }}>Loading MLS credentials status…</div>
      </div>
    );
  }
  if (!status) return null;

  // KOPS-620: Vendor feed toggle handlers
  const handleVendorActivate = async () => {
    setSaving(true);
    setAlert(null);
    try {
      await axiosInstance.post(`/admin/subscribers/${subscriberId}/mls-vendor-feed/activate`, {
        reason: 'admin_manual_activation',
      });
      setAlert({ type: 'success', message: 'Vendor feed activated' });
      const res = await axiosInstance.get(`/admin/subscribers/${subscriberId}/mls-credentials/status`);
      setStatus(res.data?.data || null);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err?.response?.data?.error || 'Activation failed' });
    } finally {
      setSaving(false);
      setTimeout(() => setAlert(null), 5000);
    }
  };

  const handleVendorSuspend = async () => {
    if (!window.confirm('Suspend MLS vendor feed access? This subscriber will lose MLS features immediately.')) return;
    setSaving(true);
    setAlert(null);
    try {
      await axiosInstance.post(`/admin/subscribers/${subscriberId}/mls-vendor-feed/suspend`, {
        reason: 'admin_manual_suspension',
      });
      setAlert({ type: 'success', message: 'Vendor feed suspended' });
      const res = await axiosInstance.get(`/admin/subscribers/${subscriberId}/mls-credentials/status`);
      setStatus(res.data?.data || null);
    } catch (err: any) {
      setAlert({ type: 'danger', message: err?.response?.data?.error || 'Suspension failed' });
    } finally {
      setSaving(false);
      setTimeout(() => setAlert(null), 5000);
    }
  };

  // KOPS-620: If vendor feed status is returned, render vendor feed card instead of legacy credentials
  if (status.feed_type === 'vendor' || status.mls_feed_type === 'vendor' || status.mls_feed_type === 'suspended') {
    const isActive = status.mls_feed_type === 'vendor';
    return (
      <div
        style={{
          background: '#162844',
          borderRadius: '12px',
          padding: '18px 20px',
          marginBottom: '20px',
          border: `1px solid ${isActive ? '#27ae60' : '#e74c3c'}`,
          borderLeft: `4px solid ${isActive ? '#27ae60' : '#e74c3c'}`,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <div>
            <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
              MLS Vendor Feed Access
            </h6>
            <div style={{ fontSize: '11px', color: '#8FA3BF', marginTop: '2px' }}>
              KairosIQ ARMLS Vendor Feed · $150/yr annual renewal
            </div>
          </div>
          <span
            style={{
              fontSize: '10px',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: '4px',
              textTransform: 'uppercase',
              background: isActive ? 'rgba(39, 174, 96, 0.15)' : 'rgba(231, 76, 60, 0.15)',
              color: isActive ? '#27ae60' : '#e74c3c',
              border: `1px solid ${isActive ? '#27ae60' : '#e74c3c'}`,
            }}
          >
            {isActive ? 'Active' : 'Suspended'}
          </span>
        </div>

        {alert && (
          <Alert variant={alert.type} className="py-2 mb-2" style={{ fontSize: '12px' }}>
            {alert.message}
          </Alert>
        )}

        <div style={{ fontSize: '13px', color: '#E8EEF6' }}>
          {status.mls_activation_date && (
            <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
              <span style={{ color: '#8FA3BF' }}>Activated</span>
              <span>{new Date(status.mls_activation_date).toLocaleDateString()}</span>
            </div>
          )}
          <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
            <span style={{ color: '#8FA3BF' }}>Feed Type</span>
            <span>KairosIQ Vendor Feed (Shared)</span>
          </div>
          <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
            <span style={{ color: '#8FA3BF' }}>Billing</span>
            <span>$150/yr via Stripe (auto-renews)</span>
          </div>
          {status.last_compliance_check && (
            <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
              <span style={{ color: '#8FA3BF' }}>Last Compliance Check</span>
              <span style={{ color: status.last_compliance_check.check_result === 'active' ? '#27ae60' : '#e74c3c' }}>
                {status.last_compliance_check.check_result === 'active' ? 'Passed' : 'Failed'}
                {' · '}
                {new Date(status.last_compliance_check.checked_at).toLocaleDateString()}
              </span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
          {isActive ? (
            <Button
              size="sm"
              variant="outline-danger"
              style={{ fontSize: '12px' }}
              onClick={handleVendorSuspend}
              disabled={saving}
            >
              {saving ? 'Processing…' : 'Suspend Access'}
            </Button>
          ) : (
            <Button
              size="sm"
              style={{ background: '#27ae60', border: 'none', fontSize: '12px' }}
              onClick={handleVendorActivate}
              disabled={saving}
            >
              {saving ? 'Processing…' : 'Activate Access'}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        background: '#162844',
        borderRadius: '12px',
        padding: '18px 20px',
        marginBottom: '20px',
        border: `1px solid ${
          status.configured ? '#C9952A'
          : status.inherited_from ? '#3498db'
          : '#1E3557'
        }`,
        borderLeft: `4px solid ${
          status.configured ? '#27ae60'
          : status.inherited_from ? '#3498db'
          : '#8FA3BF'
        }`,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div>
          <h6 style={{ color: '#C9952A', fontSize: '12px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', margin: 0 }}>
            ARMLS API Credentials
          </h6>
          <div style={{ fontSize: '11px', color: '#8FA3BF', marginTop: '2px' }}>
            Stored in AWS Secrets Manager · inherited by brokerage agents
          </div>
        </div>
        <span
          style={{
            fontSize: '10px',
            fontWeight: 700,
            padding: '4px 10px',
            borderRadius: '4px',
            textTransform: 'uppercase',
            background: status.configured ? 'rgba(39, 174, 96, 0.15)'
              : status.inherited_from ? 'rgba(52, 152, 219, 0.15)'
              : 'rgba(143, 163, 191, 0.15)',
            color: status.configured ? '#27ae60'
              : status.inherited_from ? '#3498db'
              : '#8FA3BF',
            border: `1px solid ${
              status.configured ? '#27ae60'
              : status.inherited_from ? '#3498db'
              : '#8FA3BF'
            }`,
          }}
        >
          {status.configured
            ? '✓ Configured'
            : status.inherited_from
            ? '↳ Inherited'
            : 'Not Configured'}
        </span>
      </div>

      {alert && (
        <Alert variant={alert.type} className="py-2 mb-2" style={{ fontSize: '12px' }}>
          {alert.message}
        </Alert>
      )}

      {!editing && status.configured && (
        <div style={{ fontSize: '13px', color: '#E8EEF6' }}>
          <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
            <span style={{ color: '#8FA3BF' }}>OAuth Key</span>
            <span style={{ fontFamily: 'monospace' }}>{status.masked_oauth_key || '••••'}</span>
          </div>
          {status.last_updated && (
            <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
              <span style={{ color: '#8FA3BF' }}>Last Updated</span>
              <span>{new Date(status.last_updated).toLocaleString()}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <Button
              size="sm"
              style={{ background: '#C9952A', border: 'none', fontSize: '12px' }}
              onClick={() => setEditing(true)}
            >
              Update Credentials
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              style={{ fontSize: '12px' }}
              onClick={handleDelete}
              disabled={saving}
            >
              Remove
            </Button>
          </div>
        </div>
      )}

      {!editing && !status.configured && status.inherited_from && (
        <div>
          <div style={{ fontSize: '13px', color: '#E8EEF6' }}>
            <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
              <span style={{ color: '#8FA3BF' }}>Inherited From</span>
              <span style={{ fontWeight: 600 }}>{status.inherited_from.brokerage_name || 'Brokerage'}</span>
            </div>
            <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
              <span style={{ color: '#8FA3BF' }}>Broker Admin</span>
              <span>{status.inherited_from.admin_name || status.inherited_from.admin_email || '—'}</span>
            </div>
            {status.inherited_from.masked_oauth_key && (
              <div className="d-flex justify-content-between py-1" style={{ borderBottom: '1px solid #1E3557' }}>
                <span style={{ color: '#8FA3BF' }}>OAuth Key</span>
                <span style={{ fontFamily: 'monospace' }}>{status.inherited_from.masked_oauth_key}</span>
              </div>
            )}
          </div>
          <p style={{ fontSize: '11px', color: '#8FA3BF', marginTop: '10px', marginBottom: '10px', lineHeight: 1.5 }}>
            This agent inherits ARMLS access from their brokerage's seat.
            Per platform policy, team members <strong>always</strong> use the
            brokerage's credentials — personal keys on a team member are
            ignored at runtime. To give this agent independent access, remove
            them from the brokerage team first.
          </p>
        </div>
      )}

      {!editing && !status.configured && !status.inherited_from && (
        <div>
          <p style={{ fontSize: '12px', color: '#8FA3BF', marginBottom: '10px' }}>
            No MLS access configured for this subscriber.
          </p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              size="sm"
              style={{ background: '#27ae60', border: 'none', fontSize: '12px' }}
              onClick={handleVendorActivate}
              disabled={saving}
            >
              {saving ? 'Processing…' : 'Activate Vendor Feed'}
            </Button>
            <Button
              size="sm"
              style={{ background: '#C9952A', border: 'none', fontSize: '12px' }}
              onClick={() => setEditing(true)}
            >
              + Add Custom Credentials
            </Button>
          </div>
        </div>
      )}

      {editing && (
        <div>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '11px', color: '#8FA3BF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              OAuth Key / API Key *
            </Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={form.oauth_key}
              onChange={(e) => setForm({ ...form, oauth_key: e.target.value })}
              placeholder="ARMLS OAuth client ID"
              style={{ background: '#1E3557', border: '1px solid #1E3557', color: '#E8EEF6', fontSize: '12px' }}
              autoFocus
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '11px', color: '#8FA3BF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Access Token *
            </Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={form.access_token}
              onChange={(e) => setForm({ ...form, access_token: e.target.value })}
              placeholder="Current ARMLS access token"
              style={{ background: '#1E3557', border: '1px solid #1E3557', color: '#E8EEF6', fontSize: '12px' }}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label style={{ fontSize: '11px', color: '#8FA3BF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Refresh Token
            </Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={form.refresh_token}
              onChange={(e) => setForm({ ...form, refresh_token: e.target.value })}
              placeholder="Optional — for automatic token refresh"
              style={{ background: '#1E3557', border: '1px solid #1E3557', color: '#E8EEF6', fontSize: '12px' }}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontSize: '11px', color: '#8FA3BF', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              API URL
            </Form.Label>
            <Form.Control
              size="sm"
              type="text"
              value={form.api_url}
              onChange={(e) => setForm({ ...form, api_url: e.target.value })}
              style={{ background: '#1E3557', border: '1px solid #1E3557', color: '#E8EEF6', fontSize: '12px' }}
            />
          </Form.Group>
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button
              size="sm"
              style={{ background: '#C9952A', border: 'none', fontSize: '12px' }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Spinner size="sm" /> : '💾 Save to AWS'}
            </Button>
            <Button
              size="sm"
              variant="outline-light"
              style={{ borderColor: '#8FA3BF', color: '#8FA3BF', fontSize: '12px' }}
              onClick={() => {
                setEditing(false);
                setAlert(null);
              }}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const BrokerageRelationshipCard: React.FC<{
  relationship: BrokerageRelationship | null;
  individualPlan: string;
  userId?: string;
}> = ({ relationship, individualPlan, userId }) => {
  const teamPlanMeta: Record<string, { color: string; price: number }> = {
    'team-member': { color: '#27ae60', price: 0 },
    'agent-hub':   { color: '#8FA3BF', price: 70 },
    'ai-search':   { color: '#3498db', price: 135 },
    'ai-pro':      { color: '#C9952A', price: 185 },
  };

  if (!relationship) {
    return (
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '16px 18px',
          border: '1px solid #e9ecef',
          borderLeft: '4px solid #8FA3BF',
          marginBottom: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'rgba(143,163,191,0.15)',
              border: '2px solid rgba(143,163,191,0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              fontSize: '18px',
            }}
          >
            👤
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 700,
                color: '#8FA3BF',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginBottom: '3px',
              }}
            >
              Brokerage Relationship
            </div>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#0F1E35' }}>
              Solo Subscriber
            </div>
            <div style={{ fontSize: '12px', color: '#8FA3BF', marginTop: '2px' }}>
              Not under any broker umbrella — pays for their own subscription directly
            </div>
          </div>
        </div>
      </div>
    );
  }

  const meta = teamPlanMeta[relationship.team_plan] || teamPlanMeta['team-member'];
  const teamPlanLabel = TEAM_PLAN_LABELS[relationship.team_plan] || relationship.team_plan;
  const teamPlanIncludes = TEAM_PLAN_INCLUDES[relationship.team_plan] || '';

  // Calculate the stacked math
  const individualPrice = PLAN_PRICES[individualPlan] || 0;
  const brokerCoverage = meta.price;
  const totalValue = individualPrice + brokerCoverage;

  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '16px 18px',
        border: '1px solid #e9ecef',
        borderLeft: `4px solid ${meta.color}`,
        marginBottom: '20px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '14px' }}>
        <div
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            background: `${meta.color}22`,
            border: `2px solid ${meta.color}66`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '20px',
          }}
        >
          🏢
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '11px',
              fontWeight: 700,
              color: '#8FA3BF',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              marginBottom: '3px',
            }}
          >
            Brokerage Relationship
          </div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F1E35' }}>
            {relationship.brokerage_name}
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
            <span
              style={{
                background: relationship.is_admin ? '#C9952A22' : '#3498db22',
                color: relationship.is_admin ? '#C9952A' : '#3498db',
                fontSize: '10px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              {relationship.is_admin ? '👑 Admin' : relationship.member_role}
            </span>
            <span
              style={{
                background: `${meta.color}22`,
                color: meta.color,
                fontSize: '10px',
                fontWeight: 700,
                padding: '2px 8px',
                borderRadius: '4px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Team Plan: {teamPlanLabel}
            </span>
          </div>
        </div>
      </div>

      <div
        style={{
          background: '#f8f9fa',
          borderRadius: '8px',
          padding: '10px 14px',
          fontSize: '12px',
          color: '#555',
          lineHeight: 1.5,
          marginBottom: '10px',
        }}
      >
        <strong style={{ color: '#0F1E35' }}>Included in broker plan:</strong>{' '}
        {teamPlanIncludes}
      </div>

      {/* Stacked math row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: '8px',
          background: '#f8f9fa',
          borderRadius: '8px',
          padding: '10px 14px',
        }}
      >
        <div>
          <div style={{ fontSize: '9px', color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Broker Coverage
          </div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: meta.color }}>
            ${brokerCoverage}/mo
          </div>
        </div>
        <div>
          <div style={{ fontSize: '9px', color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Agent Out-of-Pocket
          </div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#0F1E35' }}>
            ${individualPrice}/mo
          </div>
        </div>
        <div>
          <div style={{ fontSize: '9px', color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            Total Value
          </div>
          <div style={{ fontSize: '16px', fontWeight: 700, color: '#27ae60' }}>
            ${totalValue}/mo
          </div>
        </div>
      </div>

      {/* KOPS-468 — Screen Access toggles */}
      {userId && <ScreenAccessToggles userId={userId} memberRole={relationship.member_role} isAdmin={relationship.is_admin} />}

      <div style={{ marginTop: '10px', textAlign: 'right' }}>
        <a
          href={`/broker-intelligence-v2/agents?id=${relationship.team_member_id}`}
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#C9952A',
            textDecoration: 'none',
          }}
        >
          🏢 Manage in Broker Intelligence →
        </a>
      </div>
    </div>
  );
};

// KOPS-468 — Screen Access Toggles for a user
const ScreenAccessToggles: React.FC<{
  userId: string;
  memberRole?: string;
  isAdmin?: boolean;
}> = ({ userId, memberRole, isAdmin }) => {
  const [access, setAccess] = useState<Record<string, boolean>>({
    agentPortal: true,
    brokerPortal: isAdmin || memberRole === 'admin' ? true : false,
    brokerIntelligenceV2: isAdmin || memberRole === 'admin' ? true : false,
    adminSubscribers: false,
    adminContacts: false,
    adminEvents: false,
    adminSponsors: false,
    countyDeals: false,
  });
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load current screen access on mount
  useEffect(() => {
    axiosInstance.get(`/admin/subscribers/${userId}/screen-access`)
      .then(r => {
        if (r.data?.success && r.data?.screenAccess) {
          const sa = r.data.screenAccess;
          const isOn = (key: string) => sa[key]?.level === 'full' || sa[key]?.level === 'read';
          setAccess({
            agentPortal: isOn('agentPortal') || true,
            brokerPortal: isOn('brokerPortal') || (isAdmin || memberRole === 'admin'),
            brokerIntelligenceV2: isOn('brokerIntelligenceV2') || isOn('brokerIntelligence') || (isAdmin || memberRole === 'admin'),
            adminSubscribers: isOn('adminSubscribers'),
            adminContacts: isOn('adminContacts'),
            adminEvents: isOn('adminEvents'),
            adminSponsors: isOn('adminSponsors'),
            countyDeals: isOn('countyDeals'),
          });
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, [userId]);

  const handleToggle = async (key: string) => {
    const newValue = !access[key];
    setAccess(prev => ({ ...prev, [key]: newValue }));
    setSaving(true);
    try {
      await axiosInstance.put(`/admin/subscribers/${userId}/screen-access`, {
        screens: { [key]: newValue ? 'full' : 'none' }
      });
    } catch (err) {
      console.error('Failed to update screen access:', err);
      setAccess(prev => ({ ...prev, [key]: !newValue })); // revert
    } finally {
      setSaving(false);
    }
  };

  const screens = [
    { key: 'agentPortal', label: 'Agent Portal', desc: 'Documents, transactions, compliance' },
    { key: 'brokerPortal', label: 'Broker Portal', desc: 'Document review, signing, templates' },
    { key: 'brokerIntelligenceV2', label: 'Broker Intelligence', desc: 'Admin dashboard, agents, billing' },
    { key: 'adminSubscribers', label: 'Subscribers', desc: 'Manage subscriber accounts' },
    { key: 'adminContacts', label: 'Contacts', desc: 'Contact hub, SMS, automation' },
    { key: 'adminEvents', label: 'Events', desc: 'Event management, registrations' },
    { key: 'adminSponsors', label: 'Sponsors', desc: 'Sponsor account management' },
    { key: 'countyDeals', label: 'County Deals', desc: 'County intelligence dashboard' },
  ];

  return (
    <div style={{ background: '#f0f4f8', borderRadius: '8px', padding: '12px 14px', marginTop: '10px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, color: '#8FA3BF', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
        Screen Access
      </div>
      {screens.map(s => (
        <div key={s.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: s.key !== 'brokerIntelligenceV2' ? '1px solid #e2e8f0' : 'none' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#0F1E35' }}>{s.label}</div>
            <div style={{ fontSize: '10px', color: '#8FA3BF' }}>{s.desc}</div>
          </div>
          <button
            onClick={() => handleToggle(s.key)}
            disabled={saving}
            style={{
              width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: saving ? 'not-allowed' : 'pointer',
              background: access[s.key] ? '#27ae60' : '#ccc', position: 'relative', transition: 'background 0.2s',
            }}
          >
            <div style={{
              width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '2px',
              left: access[s.key] ? '22px' : '2px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </button>
        </div>
      ))}
    </div>
  );
};

export default SubscribersPage;
